"use client";

// LOT 3.5 - Reps : rendu des types d'exercice, game-feel facon Duolingo/Mimo.
// Theme CLAIR par defaut + dark: conserve (le projet est en darkMode "class").
// NB framer-motion v6 : useAnimation (pas useAnimate). Vol de tuile via layoutId
// a UN SEUL noeud monte (la chip quitte la banque, placeholder invisible sans layoutId).

import { Fragment, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import {
    TOKEN_DARK,
    TOKEN_LIGHT,
    codeLang,
    fileNameFor,
    looksLikeHtml,
    panelFileName,
    sliceTokens,
    tokenizeLines,
    tokenizeParts,
} from "@/lib/reps/highlight";
import { baseTag } from "@/lib/reps/previewAssets";
import { actionsOf, liveDoc, storageOf } from "@/lib/reps/livePage";
import { normaliserSaisieCode } from "@/lib/reps/saisieCode";
import {
    corrigerSaisieMonaco,
    forcerFinsDeLigneLF,
    neutraliserDefilementAuFocus,
} from "@/lib/monacoEditeur";
import {
    RICH_RE,
    splitBlocks,
    splitEmphasis,
    splitLinks,
} from "@/lib/reps/mdlite";
import {
    assemblePage,
    documentTitle,
    entryFileName,
    fileLanguage,
    fileMapOf,
    isCreatableFileName,
    navigationTarget,
    normalizeFileName,
    renameFileInMap,
} from "@/lib/reps/multifile";
import {
    motion,
    AnimatePresence,
    LayoutGroup,
    useReducedMotion,
    useAnimation,
} from "motion/react";
import { Icon } from "@iconify/react";
import dynamic from "next/dynamic";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import PublicLottieAnimation from "@/components/PublicLottieAnimation/PublicLottieAnimation";
import { runReps, warmReps } from "@/lib/reps/pyodideRunner";
import { runJs, warmJs } from "@/lib/reps/jsRunner";

// UN SEUL POINT D'ENTRÉE POUR EXÉCUTER LE CODE D'UNE LEÇON, quel que soit son
// langage : les consoles (exercice, concept exécuté, révélation) ne savent pas
// quel runtime tourne derrière. Python passe par Pyodide, JavaScript par un
// worker jeté après chaque exécution (lib/reps/jsRunner.js).
const executableLang = (l) => (l === "python" || l === "javascript" ? l : null);
const runCodeFor = (l, code, inputs) =>
    l === "javascript" ? runJs(code) : runReps(code, inputs);
const warmFor = (l) => (l === "javascript" ? warmJs() : warmReps());
const commandFor = (l) => (l === "javascript" ? "node" : "python");

// Meme editeur que les challenges interactifs (Monaco / VS Code), charge a la demande.
function MonacoLoading() {
    const t = useTranslations();
    return (
        <div className="flex h-[190px] items-center justify-center bg-[#1e1e1e] text-xs text-gray-400">
            {t("reps_editor_loading")}
        </div>
    );
}
const MonacoEditor = dynamic(() => import("@monaco-editor/react"), {
    ssr: false,
    loading: () => <MonacoLoading />,
});

// Compteur d'instances d'éditeur : préfixe des chemins de modèles Monaco des
// leçons multi-fichiers (voir RepsCodeEditor).
let monacoModelSeq = 0;

// fileNameFor / panelFileName vivent dans le module de coloration, à côté de
// codeLang : le nom affiché découle de la langue effective, et les deux
// décisions doivent rester une seule paire, comparable entre les deux players.

// Icône de type de fichier (façon vrai éditeur) : reconnue à l'extension.
// Iconify (comme le logo Chrome de l'aperçu) : Solar n'a pas d'icônes de
// langages, et c'est exactement le cas où une icône de marque est le bon choix.
function fileIconFor(name) {
    const n = String(name || "").toLowerCase();
    if (n.endsWith(".css")) return "vscode-icons:file-type-css";
    if (n.endsWith(".js")) return "vscode-icons:file-type-js-official";
    if (n.endsWith(".py")) return "vscode-icons:file-type-python";
    return "vscode-icons:file-type-html";
}

// La saisie est assainie avant tout le reste, et le modèle corrigé sur place :
// voir lib/monacoEditeur.js (corrigerSaisieMonaco) et handleChange ci-dessous.

function RepsCodeEditor({
    value,
    onChange,
    disabled,
    readOnly,
    border,
    className,
    style,
    language = "html",
    filename,
    roHint,
    autoHeight,
    // Leçons MULTI-FICHIERS : l'en-tête devient une rangée d'onglets (un par
    // fichier), comme dans un vrai éditeur. `tabs` = noms canoniques dans
    // l'ordre d'affichage ; l'appelant porte l'état du fichier actif.
    tabs,
    activeTab,
    onSelectTab,
    // CRÉATION de fichiers par l'apprenant (exercices allowCreate) :
    // onAddTab(nom) rend true si le nom est accepté ; closableTabs = les
    // fichiers qu'il a créés lui-même (les seuls qu'il peut retirer, et les
    // seuls qu'il peut RENOMMER — un fichier fourni par l'exercice garde son
    // nom, sans quoi la solution viserait un fichier disparu).
    onAddTab,
    closableTabs,
    onCloseTab,
    onRenameTab,
}) {
    const t = useTranslations();
    const ro = readOnly || disabled;
    const label = filename === null ? null : filename || fileNameFor(language);
    // MULTI-FICHIERS : un modèle Monaco PAR onglet, via la prop `path` du
    // wrapper — c'est elle qui donne à chaque fichier son historique d'undo,
    // son curseur et son scroll. Sans elle, changer d'onglet réécrivait la
    // valeur du modèle unique : Cmd+Z après un changement d'onglet restaurait
    // le texte de L'ANCIEN fichier dans le nouveau. Le préfixe unique par
    // montage évite qu'une autre leçon (elles s'appellent toutes index.html)
    // réutilise le même modèle global et son historique.
    const seqRef = useRef(null);
    if (seqRef.current === null) seqRef.current = ++monacoModelSeq;
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const enCompositionRef = useRef(false);
    const tabsMode = Array.isArray(tabs) && (tabs.length > 1 || !!onAddTab);
    // Onglets disparus (fichier retiré ou renommé) : leur modèle Monaco
    // survivrait dans le registre global et, l'éditeur n'étant plus contrôlé
    // (voir defaultValue plus bas), un fichier recréé sous le même nom
    // réafficherait l'ancien contenu. Le wrapper a déjà basculé sur l'onglet
    // actif quand cet effet tourne (effets enfants d'abord) : on ne détruit
    // jamais le modèle attaché.
    const tabsPrecedentsRef = useRef(tabs);
    useEffect(() => {
        const avant = tabsPrecedentsRef.current;
        tabsPrecedentsRef.current = tabs;
        const monaco = monacoRef.current;
        if (!monaco || !Array.isArray(avant) || !Array.isArray(tabs)) return;
        avant
            .filter((name) => !tabs.includes(name))
            .forEach((name) => {
                const model = monaco.editor.getModel(
                    monaco.Uri.parse(`reps-${seqRef.current}/${name}`)
                );
                if (model && !model.isDisposed()) model.dispose();
            });
    }, [tabs]);
    // Mini-formulaire de création de fichier, dans la barre d'onglets. Le même
    // champ sert à RENOMMER : `renaming` porte alors l'ancien nom, et c'est la
    // seule différence entre les deux gestes.
    const [addingFile, setAddingFile] = useState(false);
    const [renaming, setRenaming] = useState(null);
    const [newFileName, setNewFileName] = useState("");
    const [newFileBad, setNewFileBad] = useState(false);
    const fermerSaisie = () => {
        setAddingFile(false);
        setRenaming(null);
        setNewFileName("");
        setNewFileBad(false);
    };
    function submitNewFile() {
        const ok = renaming
            ? onRenameTab && onRenameTab(renaming, newFileName)
            : onAddTab && onAddTab(newFileName);
        if (ok) fermerSaisie();
        else setNewFileBad(true);
    }
    function demarrerRenommage(name) {
        setRenaming(name);
        setAddingFile(false);
        setNewFileName(name);
        setNewFileBad(false);
    }
    // `autoHeight` : l'éditeur prend la hauteur RÉELLE de son contenu, mesurée
    // par Monaco après repli des lignes. Une estimation au nombre de caractères
    // se trompe forcément (la largeur change avec l'écran) et coupe le code sur
    // mobile. Bornée, pour ne jamais manger l'écran.
    const [measuredH, setMeasuredH] = useState(null);
    // Monaco affiche sinon son « Cannot edit in read-only editor » en ANGLAIS
    // quand on tape dans un editeur verrouille. Messages par defaut NEUTRES
    // (vrais dans tous les cas : bonne comme mauvaise reponse, apercu admin) ;
    // l'appelant precise via `roHint` quand il en sait plus (ex. reponse
    // fausse -> la question reviendra, code pilote par des reglages).
    const roMessage =
        roHint ||
        (disabled
            ? t("reps_editor_locked_answered")
            : t("reps_editor_readonly_example"));
    // Options mémorisées et gestionnaire stable : le wrapper rejoue
    // editor.updateOptions à chaque nouvelle identité de `options` et se
    // réabonne à onDidChangeModelContent à chaque nouvelle identité de
    // `onChange`, dans des effets qui tournaient à CHAQUE frappe (le player
    // entier se re-rend à chaque caractère). Du travail perdu qui allongeait
    // le rendu d'une frappe.
    const options = useMemo(
        () => ({
            readOnly: ro,
            domReadOnly: ro,
            readOnlyMessage: { value: roMessage },
            // Pas d'autocompletion : la liste deroulante etait coupee.
            quickSuggestions: false,
            suggestOnTriggerCharacters: false,
            wordBasedSuggestions: "off",
            acceptSuggestionOnEnter: "off",
            tabCompletion: "off",
            parameterHints: { enabled: false },
            hover: { enabled: false },
            suggest: { showWords: false },
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            wordWrap: "on",
            // Python s'écrit à QUATRE espaces, et c'est ce que le
            // contenu enseigne : un Tab qui en insérait deux
            // contredisait la leçon au moment précis du geste.
            // Le HTML reste à deux, comme ses gabarits.
            tabSize: language === "python" ? 4 : 2,
            insertSpaces: true,
            automaticLayout: true,
            padding: { top: 12 },
            lineHeight: 22,
            fontFamily:
                "'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, monospace",
            renderLineHighlight: "none",
            scrollbar: {
                verticalScrollbarSize: 8,
                horizontalScrollbarSize: 8,
            },
            overviewRulerBorder: false,
            hideCursorInOverviewRuler: true,
            cursorBlinking: "smooth",
            smoothScrolling: true,
            // Pas d'animation du caret. Dans Monaco 0.55 elle n'est
            // qu'une transition CSS de 80 ms sur la position du
            // curseur : elle ne « prend pas de retard », mais elle
            // rend visible chaque saut LOGIQUE du curseur, dont le
            // saut en fin de document que l'éditeur contrôlé
            // provoquait (voir defaultValue ci-dessous). Sans elle,
            // le caret est posé avec le texte, dans la même passe
            // de rendu.
            cursorSmoothCaretAnimation: "off",
        }),
        [ro, roMessage, language]
    );
    const onChangeRef = useRef(onChange);
    // En effet de layout, pas pendant le rendu : synchrone dans le commit,
    // donc aucune frappe ne peut se glisser entre le rendu et la mise à jour.
    useLayoutEffect(() => {
        onChangeRef.current = onChange;
    });
    const handleChange = useCallback((v, ev) => {
        const onChange = onChangeRef.current;
        if (!onChange) return;
        const brut = v || "";
        // Cmd+Z restaure la frappe d'origine : re-corriger ICI
        // rejouerait la correction à chaque annulation (redo
        // détruit, l'apprenant coincé au point de correction —
        // un collage type PasteOperation est un élément d'undo
        // FERMÉ, notre édition en est un second). On laisse
        // passer : l'annulation suivante atteint l'état
        // d'avant le collage, et le premier geste ordinaire
        // ré-assainit.
        if (ev && (ev.isUndoing || ev.isRedoing)) {
            onChange(brut);
            return;
        }
        // Pendant une composition IME (touche morte, pinyin,
        // diacritique combinant), le buffer vit DANS le
        // modèle : le corriger en vol vole l'accent à la
        // lettre en cours. Rejoué à onDidCompositionEnd
        // (onMount).
        if (enCompositionRef.current) {
            onChange(brut);
            return;
        }
        const propre = normaliserSaisieCode(brut);
        if (propre === brut) {
            onChange(brut);
            return;
        }
        // Le modèle est corrigé sur place ; ce qui remonte à React est ce
        // que le modèle contient VRAIMENT après correction.
        const reel = corrigerSaisieMonaco(editorRef.current, propre);
        onChange(reel == null ? propre : reel);
    }, []);
    return (
        <div
            className={`flex flex-col overflow-hidden rounded-xl border ${
                border || "border-gray-200 dark:border-white/10"
            } ${className || ""}`}
            style={
                autoHeight && measuredH
                    ? { ...(style || {}), height: measuredH }
                    : style
            }
        >
            {tabsMode ? (
                <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-[#2d2d2d] bg-[#1e1e1e] px-2 pt-1.5">
                    {tabs.map((name) => (
                        <button
                            key={name}
                            type="button"
                            onClick={() => onSelectTab && onSelectTab(name)}
                            // Double-clic = renommer, comme dans un vrai
                            // éditeur. Le crayon reste là pour ceux qui ne
                            // connaissent pas le geste (et pour le tactile).
                            onDoubleClick={() => {
                                if (
                                    onRenameTab &&
                                    Array.isArray(closableTabs) &&
                                    closableTabs.includes(name)
                                )
                                    demarrerRenommage(name);
                            }}
                            className={`flex shrink-0 items-center gap-1.5 rounded-t-md border-b-2 px-2.5 py-1.5 font-mono text-xs transition-colors ${
                                name === activeTab
                                    ? "border-orange-500 bg-white/[0.06] text-gray-100"
                                    : "border-transparent text-gray-500 hover:text-gray-300"
                            }`}
                        >
                            <Icon
                                icon={fileIconFor(name)}
                                style={{ fontSize: "13px" }}
                                aria-hidden
                            />
                            {name}
                            {onRenameTab &&
                                Array.isArray(closableTabs) &&
                                closableTabs.includes(name) && (
                                    <span
                                        role="button"
                                        tabIndex={-1}
                                        title={t("reps_file_rename")}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            demarrerRenommage(name);
                                        }}
                                        className="rounded px-0.5 text-gray-500 hover:bg-white/10 hover:text-gray-200"
                                    >
                                        <Icon
                                            icon="solar:pen-linear"
                                            style={{ fontSize: "11px" }}
                                            aria-hidden
                                        />
                                    </span>
                                )}
                            {onCloseTab &&
                                Array.isArray(closableTabs) &&
                                closableTabs.includes(name) && (
                                    <span
                                        role="button"
                                        tabIndex={-1}
                                        title={t("reps_file_remove")}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCloseTab(name);
                                        }}
                                        className="rounded px-0.5 text-gray-500 hover:bg-white/10 hover:text-gray-200"
                                    >
                                        ×
                                    </span>
                                )}
                        </button>
                    ))}
                    {(onAddTab || renaming) &&
                        (addingFile || renaming ? (
                            <span className="flex shrink-0 items-center gap-1 pb-0.5">
                                <input
                                    autoFocus
                                    value={newFileName}
                                    onChange={(e) => {
                                        setNewFileName(e.target.value);
                                        setNewFileBad(false);
                                    }}
                                    onKeyDown={(e) => {
                                        // Le player écoute Entrée (= Vérifier)
                                        // et Échap au niveau de la fenêtre :
                                        // nommer un fichier ne doit JAMAIS
                                        // soumettre l'exercice ni le quitter.
                                        e.stopPropagation();
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            submitNewFile();
                                        }
                                        if (e.key === "Escape") fermerSaisie();
                                    }}
                                    onBlur={() => {
                                        if (!newFileName.trim()) fermerSaisie();
                                    }}
                                    placeholder="page.html"
                                    spellCheck={false}
                                    className={`w-32 rounded-md border bg-[#141414] px-2 py-1 font-mono text-xs text-gray-100 outline-hidden ${
                                        newFileBad
                                            ? "border-red-500"
                                            : "border-[#3a3a3a] focus:border-orange-500"
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={submitNewFile}
                                    className="rounded-md bg-white/10 px-2 py-1 text-xs text-gray-200 hover:bg-white/15"
                                >
                                    OK
                                </button>
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setAddingFile(true)}
                                title={t("reps_file_add")}
                                className="shrink-0 rounded-t-md border-b-2 border-transparent px-2 py-1.5 text-sm leading-none text-gray-500 hover:text-gray-200"
                            >
                                +
                            </button>
                        ))}
                </div>
            ) : label === null ? null : (
                <div className="flex shrink-0 items-center gap-2 border-b border-[#2d2d2d] bg-[#1e1e1e] px-3 py-2">
                    <Icon
                        icon={fileIconFor(label)}
                        style={{ fontSize: "13px" }}
                        aria-hidden
                    />
                    <span className="text-xs text-gray-300">{label}</span>
                </div>
            )}
            <div className="flex-1 bg-[#1e1e1e]">
                <MonacoEditor
                    height="100%"
                    path={tabsMode ? `reps-${seqRef.current}/${activeTab}` : undefined}
                    language={language}
                    theme="vs-dark"
                    // Éditeur NON CONTRÔLÉ pour l'apprenant : le modèle Monaco
                    // est la seule source de vérité, React n'en reçoit qu'une
                    // copie (onChange). Avec `value`, le wrapper compare à
                    // chaque rendu la valeur COMMITÉE au modèle VIVANT et, si
                    // elles diffèrent, remplace le document entier
                    // (executeEdits plein, forceMoveMarkers) : le curseur
                    // saute en fin de code. Sur Chrome/Edge la frappe passe par
                    // EditContext (événement `textupdate`, priorité « default »
                    // pour React 19, effets différés à une tâche suivante) :
                    // deux frappes rapprochées suffisaient. Signalé deux fois
                    // depuis PC (27/08 et 01/09/2026). Clé sur la prop STATIQUE
                    // `readOnly`, jamais sur `ro` : `disabled` bascule au
                    // verrouillage et déclencherait le setValue du wrapper. Les
                    // éditeurs en lecture seule (concepts, tune) restent pilotés
                    // par `value` : leur contenu vient de l'extérieur (curseurs).
                    {...(readOnly ? { value } : { defaultValue: value })}
                    onChange={handleChange}
                    options={options}
                    onMount={(editor, monaco) => {
                        editorRef.current = editor;
                        monacoRef.current = monaco;
                        // Fins de ligne LF partout (Windows crée en CRLF), et
                        // focus sans défilement de la colonne sur Chrome/Edge :
                        // voir lib/monacoEditeur.js.
                        forcerFinsDeLigneLF(editor, monaco);
                        neutraliserDefilementAuFocus(editor);
                        editor.onDidChangeConfiguration(() =>
                            neutraliserDefilementAuFocus(editor)
                        );
                        editor.onDidCompositionStart(() => {
                            enCompositionRef.current = true;
                        });
                        editor.onDidCompositionEnd(() => {
                            enCompositionRef.current = false;
                            // La normalisation différée se rejoue : les
                            // éditions notifient le modèle, donc le onChange
                            // du wrapper remonte la valeur propre tout seul.
                            const model = editor.getModel();
                            const brut = model ? model.getValue() : "";
                            const propre = normaliserSaisieCode(brut);
                            if (propre !== brut)
                                corrigerSaisieMonaco(editor, propre);
                        });
                        if (!autoHeight) return;
                        // 37 px d'en-tête (le bandeau index.html) + marge basse.
                        const sync = () =>
                            setMeasuredH(
                                Math.min(
                                    Math.max(editor.getContentHeight() + 49, 96),
                                    360
                                )
                            );
                        sync();
                        editor.onDidContentSizeChange(sync);
                    }}
                />
            </div>
        </div>
    );
}

// Cartes d'option facon QuizView Believemy : EPAISSES (border-2 + bord bas border-b-4),
// selection BLEUE, correct emerald, faux rouge, options estompees apres reponse.
const CARD =
    "w-full text-left rounded-2xl border-2 border-b-4 p-4 text-sm transition-all duration-150 disabled:cursor-default";
const C_NEUTRAL =
    "border-gray-200 border-b-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:translate-y-[2px] active:border-b-2 dark:border-grayDark-600/50 dark:border-b-grayDark-500/50 dark:bg-white/[0.04] dark:text-grayDark-300 dark:hover:bg-white/[0.07]";
const C_SEL =
    "translate-y-px border-blue-200 border-b-[3px] border-b-blue-400 bg-blue-50 text-gray-900 dark:border-blue-500/30 dark:border-b-blue-500/40 dark:bg-blue-500/10 dark:text-white";
const C_GOOD =
    "border-emerald-200 border-b-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:border-b-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200";
const C_BAD =
    "border-red-200 border-b-red-300 bg-red-50 text-red-900 dark:border-red-500/30 dark:border-b-red-500/40 dark:bg-red-500/10 dark:text-red-200";
const C_DIM =
    "border-gray-100 border-b-gray-100 bg-gray-50 text-gray-400 opacity-50 dark:border-grayDark-700/40 dark:border-b-grayDark-700/40 dark:bg-white/[0.02] dark:text-grayDark-600";

// Case à cocher des choix MULTIPLES : le carré dit, avant même de lire la
// consigne, qu'on peut en cocher plusieurs (un rond dirait le contraire).
// Cochée et fausse = case ROUGE, toujours cochée : « tu as coché ça, et c'est
// raté » se lit d'un coup, sans croix à ajouter.
const BOX_EMPTY = "h-[22px] w-[22px] shrink-0 rounded-md border-2 transition-colors";

const CHIP =
    "rounded-lg border px-3 py-2 font-geistMono text-[13px] transition-colors border-gray-200 bg-white text-gray-800 shadow-sm hover:border-gray-300 dark:border-white/10 dark:bg-white/[0.06] dark:text-grayDark-100 dark:shadow-none dark:hover:border-white/25";

// Style "code" inline aligne sur globals.css (.dynamic-content code) : orange + mono + fond leger.
const INLINE_CODE =
    "mx-0.5 rounded px-1.5 py-0.5 font-geistMono text-[0.88em] text-primary bg-primary/10";

// Le MÊME code inline, mais estompé AVEC son étiquette. Dans une étiquette
// grisée (une étiquette déjà rangée dans le vivier de drag_drop), l'orange et le
// fond primaire restaient vifs : l'étiquette avait l'air à moitié active. Ici le
// code hérite la couleur de son étiquette (text-current) et ne garde qu'un fond
// neutre, juste assez pour qu'on lise encore « c'est du code ».
const INLINE_CODE_MUTED =
    "mx-0.5 rounded px-1.5 py-0.5 font-geistMono text-[0.88em] text-current bg-gray-100 dark:bg-white/[0.06]";

function useFly() {
    const reduce = useReducedMotion();
    return reduce
        ? { duration: 0 }
        : { type: "spring", stiffness: 650, damping: 36, mass: 0.8 };
}

function isTag(t) {
    return /^<\/?[a-zA-Z][^>]*>$/.test((t || "").trim());
}

const VOID_TAG = /^<(br|img|hr|input|meta|link|area|base|col|embed|source|track|wbr)(\s|\/?>)/i;
function isOpenTag(t) {
    const s = (t || "").trim();
    return /^<[a-zA-Z][^>]*>$/.test(s) && !/\/>$/.test(s) && !VOID_TAG.test(s);
}
function isCloseTag(t) {
    return /^<\/[a-zA-Z][^>]*>$/.test((t || "").trim());
}

function buildHtml(parts, vals) {
    let out = "";
    for (let i = 0; i < parts.length; i++) {
        out += parts[i];
        if (i < parts.length - 1) out += vals[i] || "";
    }
    return out;
}

function EditorCard({ children, toolbar, border, filename }) {
    return (
        <div
            className={`flex flex-col overflow-hidden rounded-xl border bg-white dark:bg-[#10171E] ${
                border || "border-gray-200 dark:border-white/10"
            }`}
        >
            <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-white/5 dark:bg-[#192734]">
                <Icon
                    icon={fileIconFor(filename || "index.html")}
                    style={{ fontSize: "14px" }}
                    aria-hidden
                />
                <span className="text-xs text-gray-500 dark:text-grayDark-300">
                    {filename || "index.html"}
                </span>
            </div>
            <div className="min-h-[64px] flex-1 p-3.5 font-geistMono text-sm leading-relaxed text-gray-800 dark:text-grayDark-100">
                {children}
            </div>
            {toolbar && (
                <div className="flex shrink-0 gap-4 border-t border-gray-100 px-3 py-2 text-lg text-gray-400 dark:border-white/5 dark:text-grayDark-500">
                    {toolbar}
                </div>
            )}
        </div>
    );
}

// Styles par defaut du navigateur (UA) re-emules : l'app a un reset global qui
// aplatit h1/p/ul/strong..., donc on redonne un rendu "vrai navigateur" DANS
// l'iframe. Identique aux anciennes regles .reps-browser (aucune regression
// visuelle), mais desormais injecte dans le document isole de l'apercu.
// Confort d'écriture pour NOS décors (widgets, grilles de démonstration) : une
// boîte à qui l'on donne 46 px en fait 46, bordure comprise. Mais c'est un
// MENSONGE sur le CSS : sans cette ligne, un navigateur ajoute l'espace dedans
// et la bordure PAR-DESSUS la largeur demandée. Une leçon qui enseigne
// justement le modèle de boîte doit donc pouvoir s'en passer, sinon elle montre
// le contraire de ce qu'elle raconte, et `box-sizing: border-box` n'a plus
// aucun effet à l'écran. D'où data.boxModel (voir BoxModelContext).
const PREVIEW_RESET_BORDER_BOX = `*{box-sizing:border-box}\n`;

const PREVIEW_BASE_CSS = `
html,body{margin:0}
body{color:#111827;line-height:1.4;font-size:16px;word-break:break-word;font-family:'Times New Roman',Times,serif;padding:14px;background:#fff}
body>:first-child{margin-top:0}
body>:last-child{margin-bottom:0}
h1{font-size:2em;font-weight:700;margin:.67em 0;line-height:1.2}
h2{font-size:1.5em;font-weight:700;margin:.75em 0;line-height:1.25}
h3{font-size:1.17em;font-weight:700;margin:.83em 0}
h4{font-size:1em;font-weight:700;margin:1.12em 0}
h5{font-size:.83em;font-weight:700;margin:1.5em 0}
h6{font-size:.75em;font-weight:700;margin:1.67em 0}
p{margin:1em 0}
ul{list-style:disc;padding-left:40px;margin:1em 0}
ol{list-style:decimal;padding-left:40px;margin:1em 0}
li{display:list-item;margin:0}
ul ul,ol ol,ul ol,ol ul{margin:0}
a{color:#1a0dab;text-decoration:underline;cursor:pointer}
strong,b{font-weight:700}
em,i{font-style:italic}
u{text-decoration:underline}
small{font-size:.8em}
blockquote{margin:1em 40px}
hr{border:0;border-top:1px solid #ccc;margin:.5em 0}
img{max-width:100%;height:auto}
table{border-collapse:collapse}
th,td{border:1px solid #999;padding:2px 6px}
button{font-size:13.333px;padding:1px 6px;border:1px solid #767676;border-radius:3px;background:#efefef;color:#111827;cursor:pointer;font-family:sans-serif}
input,select,textarea{font-family:sans-serif}
`;

// <base target="_blank"> : les liens (et les envois de formulaire) visent une
// NOUVELLE fenêtre, que le bac à sable interdit d'ouvrir (sandbox sans
// allow-popups). Résultat : rien ne navigue, l'aperçu reste en place — mais les
// liens gardent leurs événements de pointeur, donc le curseur devient une main
// au survol et l'apprenant VOIT que c'en est un. C'est ce que `pointer-events:
// none` empêchait : il coupait aussi le survol.
function srcDoc(html, boxModel) {
    const raw = html || "";
    // `content-box` = on N'INJECTE PAS notre reset : l'aperçu se comporte comme
    // un vrai navigateur, la largeur ne mesure que le contenu. Réservé aux
    // leçons qui enseignent le modèle de boîte ; partout ailleurs, le reset.
    const reset =
        (boxModel === "content-box" ? "" : PREVIEW_RESET_BORDER_BOX) + PREVIEW_BASE_CSS;
    // <base> : un `src="lune.jpg"` d'une leçon se résout vers R2. Le contenu
    // n'écrit qu'un nom de fichier — quand le parcours enseigne les images, il
    // n'a encore rien dit d'un dossier (lib/reps/previewAssets).
    const extras = `<meta charset="utf-8"><meta name="color-scheme" content="light">${baseTag()}<style>${reset}</style>`;
    // DOCUMENT COMPLET (leçons multi-fichiers, dès que le squelette HTML5 est
    // enseigné) : l'apprenant a écrit son propre <!doctype>/<html>/<head> — on
    // ne l'enveloppe PAS dans un deuxième squelette (html imbriqué dans body :
    // exactement ce que la leçon lui apprend à ne pas faire). On glisse
    // seulement nos réglages d'aperçu au début de SON head (ou avant tout, à
    // défaut) : son propre CSS, venant après, garde le dernier mot.
    if (/^\s*(?:<!doctype\b|<html\b)/i.test(raw)) {
        const withHead = raw.replace(/<head\b[^>]*>/i, (m) => m + extras);
        if (withHead !== raw) return withHead;
        return raw.replace(/^\s*(<!doctype\b[^>]*>)?/i, (m) => m + extras);
    }
    return `<!doctype html><html><head>${extras}</head><body>${raw}</body></html>`;
}

// Hauteur d'un panneau de code. On estime les lignes RÉELLEMENT affichées : une
// ligne longue se replie et compte pour plusieurs, sinon l'éditeur paraît écrasé
// (trop court, contenu qui défile) à côté d'un aperçu aéré — d'où le sentiment
// que « l'éditeur est plus petit que la prévisualisation ». Bornes 96-360 px.
function codeHeightFor(code) {
    let visual = 0;
    for (const line of String(code || "").split("\n")) {
        visual += Math.max(1, Math.ceil(line.length / 46));
    }
    return Math.min(Math.max(65 + visual * 22, 96), 360);
}

// Apercu "navigateur" : iframe isolee (srcdoc). Isole les <style> des lecons CSS
// du reste de l'app (sinon ils fuiteraient sur toute la page), et retrouve les
// styles UA natifs. sandbox sans allow-scripts = aucun JS de lecon ne s'execute ;
// allow-same-origin sert uniquement a mesurer la hauteur du contenu.
function BrowserPane({ html, className, style, onHeight, address, onNavigate }) {
    const t = useTranslations();
    const frameRef = useRef(null);
    // Modèle de boîte de CET aperçu : posé une fois par l'étape (data.boxModel),
    // lu ici plutôt que passé en prop — treize appels à BrowserPane, et aucun
    // n'a de raison de connaître ce réglage.
    const boxModel = useContext(BoxModelContext);
    // Debounce du srcdoc : eviter un reload d'iframe a chaque frappe (write_code).
    const [doc, setDoc] = useState(() => srcDoc(html, boxModel));
    useEffect(() => {
        const id = setTimeout(() => setDoc(srcDoc(html, boxModel)), 180);
        return () => clearTimeout(id);
    }, [html, boxModel]);
    // Le titre de l'onglet suit la MÊME cadence que le rendu : lu sur le
    // document débouncé, sinon l'onglet changerait une demi-seconde avant la
    // page et l'apprenant ne relierait pas les deux.
    const docTitle = useMemo(() => documentTitle(doc), [doc]);

    function measure() {
        const f = frameRef.current;
        if (!f || !onHeight) return;
        try {
            const b = f.contentDocument;
            const h = b && b.body ? b.body.scrollHeight : 0;
            if (h) onHeight(h);
        } catch (e) {
            /* cross-origin impossible ici (srcdoc same-origin), on ignore */
        }
    }

    // NAVIGATION entre les pages d'une leçon multi-fichiers : le clic sur un
    // lien remonte au player, qui décide (page connue -> on l'affiche, ancre ou
    // lien externe -> rien). Interception depuis le PARENT via contentDocument
    // (srcdoc = même origine) : la sandbox reste SANS allow-scripts, aucun JS
    // de leçon ne s'exécute jamais. Ré-attachée à chaque load, le srcdoc
    // rechargeant l'iframe à chaque frappe débouncée.
    function attachNavigation() {
        const f = frameRef.current;
        if (!f || !onNavigate) return;
        try {
            f.contentDocument.addEventListener("click", (e) => {
                const a = e.target && e.target.closest && e.target.closest("a");
                if (!a) return;
                const href = a.getAttribute("href");
                if (!href || href.startsWith("#")) return; // ancre : natif
                e.preventDefault();
                onNavigate(href);
            });
        } catch (e) {
            /* même origine garantie ici ; au pire, pas de navigation */
        }
    }

    return (
        <div
            className={`flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-white/10 ${
                className || ""
            }`}
            style={style}
        >
            <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-white/5 dark:bg-[#192734]">
                <Icon icon="logos:chrome" style={{ fontSize: "16px" }} aria-hidden />
                {/* L'ONGLET. C'est le seul endroit où <title> se voit : sans
                    lui, la balise serait la seule du parcours dont l'effet est
                    invisible. Pas de titre = le nom du fichier, comme un vrai
                    navigateur, pour que l'apprenant voie l'onglet changer au
                    moment où il pose la balise. */}
                <span className="flex min-w-0 max-w-[60%] items-center gap-1.5 rounded-t-md bg-white px-2 py-1 shadow-sm dark:bg-white/10">
                    <SolarIcon
                        name="file-linear"
                        className="shrink-0 text-[11px] text-gray-400 dark:text-grayDark-500"
                    />
                    <span className="truncate text-xs font-medium text-gray-600 dark:text-grayDark-300">
                        {docTitle || address || "index.html"}
                    </span>
                </span>
                <span className="ml-auto hidden items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-400 dark:bg-white/10 dark:text-grayDark-500 sm:flex">
                    <SolarIcon
                        name="lock-keyhole-minimalistic-linear"
                        className="text-[10px]"
                    />
                    {address || "index.html"}
                </span>
            </div>
            <iframe
                ref={frameRef}
                title={t("reps_preview")}
                sandbox="allow-same-origin"
                srcDoc={doc}
                onLoad={() => {
                    measure();
                    attachNavigation();
                }}
                className="min-h-[120px] flex-1 border-0 bg-white"
                style={{ width: "100%", display: "block" }}
            />
        </div>
    );
}

// --- L'APERÇU VIVANT (parcours JavaScript, `data.live`) ----------------------
// Le jumeau de BrowserPane pour une page dont les scripts s'exécutent. Tout ce
// qui le rend sûr est dans lib/reps/livePage.js ; ici, on ne fait que poser
// l'iframe (allow-scripts SANS allow-same-origin) et écouter ses messages.
//
// Les deux textes collés dans la page (la console et le script d'amorce) sont
// des fichiers statiques : on les lit une fois pour toute la session.
let liveSourcesPromise = null;
function loadLiveSources() {
    if (!liveSourcesPromise) {
        liveSourcesPromise = Promise.all(
            ["/static/reps/js-console.js", "/static/reps/js-page.js"].map((u) =>
                fetch(u).then((r) => {
                    if (!r.ok) throw new Error(String(r.status));
                    return r.text();
                })
            )
        )
            .then(([c, pg]) => ({ console: c, page: pg }))
            .catch((e) => {
                liveSourcesPromise = null;
                throw e;
            });
    }
    return liveSourcesPromise;
}

let liveCanalSeq = 0;

// L'iframe seule. `onMessage` reçoit chaque message de la page COURANTE, et un
// { type: "reset" } chaque fois que le document est reconstruit.
//
// Le localStorage (section 19) : la page part de `stockage`. La page qu'on
// regarde (`garder`) retient ce qu'elle y écrit et le retrouve quand elle est
// rouverte (code modifié, bouton ↻ qui incrémente `recharge`), comme un
// navigateur qu'on recharge. La page qui rejoue les gestes repart toujours du
// stockage de l'étape ; son geste `reload` la rouvre avec ce qu'elle a écrit,
// et les gestes reprennent après lui.
function LiveFrame({ html, fileMap, actions, onMessage, hidden, title, stockage, garder, recharge }) {
    const t = useTranslations();
    const boucle = t("reps_js_loop_stopped");
    const envoi = t("reps_js_form_not_sent");
    const frameRef = useRef(null);
    const boxModel = useContext(BoxModelContext);
    const [sources, setSources] = useState(null);
    const [etat, setEtat] = useState({ doc: null, canal: "" });
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;
    const garderRef = useRef(garder);
    garderRef.current = garder;
    const cleRef = useRef("");
    const canalRef = useRef("");
    const actionsJson = JSON.stringify(actions || []);
    const stockageJson = JSON.stringify(stockage || {});
    const gardeRef = useRef(null);
    // { cle, data, depuis } : la page a demandé à être rouverte (geste reload).
    const [relance, setRelance] = useState(null);
    const cle = `${actionsJson}\u0000${stockageJson}\u0000${html}`;
    cleRef.current = cle;

    useEffect(() => {
        let alive = true;
        loadLiveSources()
            .then((x) => alive && setSources(x))
            .catch(() => alive && onMessageRef.current && onMessageRef.current({ type: "unavailable" }));
        return () => {
            alive = false;
        };
    }, []);

    // Même cadence que BrowserPane, un peu plus lente : ici chaque
    // reconstruction relance les scripts.
    useEffect(() => {
        if (!sources) return undefined;
        const id = setTimeout(() => {
            const canal = `reps-${Date.now().toString(36)}-${++liveCanalSeq}`;
            canalRef.current = canal;
            const css =
                (boxModel === "content-box" ? "" : PREVIEW_RESET_BORDER_BOX) + PREVIEW_BASE_CSS;
            const r = relance && relance.cle === cle ? relance : null;
            const tous = JSON.parse(actionsJson);
            const depart = r ? r.data : garder && gardeRef.current ? gardeRef.current : JSON.parse(stockageJson);
            const doc = liveDoc({
                html,
                fileMap,
                canal,
                actions: r ? tous.slice(r.depuis) : tous,
                css,
                head: baseTag(),
                sources,
                boucle,
                envoi,
                stockage: depart,
            });
            if (onMessageRef.current) onMessageRef.current({ type: "reset" });
            setEtat({ doc, canal });
        }, relance ? 0 : 250);
        return () => clearTimeout(id);
    }, [html, fileMap, actionsJson, stockageJson, boxModel, sources, boucle, envoi, relance, recharge, garder, cle]);

    useEffect(() => {
        function recevoir(e) {
            const f = frameRef.current;
            if (!f || e.source !== f.contentWindow) return;
            const m = e.data;
            if (!m || typeof m !== "object" || m.reps !== canalRef.current) return;
            if (m.type === "storage") {
                if (garderRef.current && m.data && typeof m.data === "object") gardeRef.current = m.data;
                return;
            }
            if (m.type === "reload") {
                const data = m.data && typeof m.data === "object" ? m.data : {};
                if (garderRef.current) gardeRef.current = data;
                setRelance({ cle: cleRef.current, data, depuis: Number(m.depuis) || 0 });
                return;
            }
            if (onMessageRef.current) onMessageRef.current(m);
        }
        window.addEventListener("message", recevoir);
        return () => window.removeEventListener("message", recevoir);
    }, []);

    if (!etat.doc) return hidden ? null : <div className="min-h-[120px] flex-1 bg-white" />;
    return (
        <iframe
            ref={frameRef}
            title={title || "Page"}
            // allow-forms : sans lui, aucun `submit` ne se déclenche (section
            // 15). L'envoi reste bloqué par `form-action 'none'` (livePage.js).
            sandbox="allow-scripts allow-forms"
            srcDoc={etat.doc}
            aria-hidden={hidden ? "true" : undefined}
            tabIndex={hidden ? -1 : undefined}
            className={hidden ? "" : "min-h-[120px] flex-1 border-0 bg-white"}
            style={
                hidden
                    ? { position: "absolute", width: 320, height: 240, left: -9999, top: 0, border: 0, visibility: "hidden" }
                    : { width: "100%", display: "block" }
            }
        />
    );
}

// Ce que la console montre d'une erreur survenue dans la page : son nom et son
// message, puis le fichier et la ligne, comme dans les outils d'un navigateur.
function liveErrorText(m) {
    const texte = String(m.texte || "Error");
    return m.fichier && m.ligne ? `${texte}\n    at ${m.fichier}:${m.ligne}` : texte;
}

// Le panneau complet : la barre du navigateur, la page, et sous elle la console
// dès que la page y a écrit quelque chose.
//   onSnapshot(dom) : la photo de la page, APRÈS les gestes de `actions` s'il y
//   en a. Les gestes sont joués dans une seconde page, invisible : celle que
//   l'apprenant regarde reste intacte, il y clique lui-même.
function LivePane({ fileMap, page, actions, stockage, className, style, onHeight, onNavigate, onSnapshot }) {
    const t = useTranslations();
    // Le bouton ↻ : la page se rouvre, son localStorage gardé (section 19).
    const [recharge, setRecharge] = useState(0);
    const html = useMemo(() => assemblePage(fileMap, page), [fileMap, page]);
    const docTitle = useMemo(() => documentTitle(html), [html]);
    const [lignes, setLignes] = useState([]);
    const gestes = Array.isArray(actions) && actions.length > 0;
    const onSnapshotRef = useRef(onSnapshot);
    onSnapshotRef.current = onSnapshot;

    function vue(m) {
        if (m.type === "reset") {
            setLignes([]);
        } else if (m.type === "log") {
            setLignes((l) => (l.length > 200 ? l : [...l, { flux: m.flux, texte: String(m.texte || "") }]));
        } else if (m.type === "error") {
            setLignes((l) => [...l, { flux: "erreur", texte: liveErrorText(m) + "\n" }]);
        } else if (m.type === "height") {
            if (onHeight) onHeight(m.h);
        } else if (m.type === "navigate") {
            if (onNavigate) onNavigate(m.href);
        } else if (m.type === "snapshot" && !gestes) {
            if (onSnapshotRef.current) onSnapshotRef.current(String(m.dom || ""));
        }
    }
    function controle(m) {
        if (m.type === "snapshot" && onSnapshotRef.current) {
            onSnapshotRef.current(String(m.dom || ""));
        }
    }

    return (
        <div
            className={`relative flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-white/10 ${
                className || ""
            }`}
            // La hauteur demandée est un PLANCHER : quand la page écrit dans la
            // console, le panneau grandit pour la montrer au lieu de la couper.
            style={{ ...style, height: undefined, minHeight: style && style.height }}
        >
            <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-white/5 dark:bg-[#192734]">
                <Icon icon="logos:chrome" style={{ fontSize: "16px" }} aria-hidden />
                <span className="flex min-w-0 max-w-[60%] items-center gap-1.5 rounded-t-md bg-white px-2 py-1 shadow-sm dark:bg-white/10">
                    <SolarIcon
                        name="file-linear"
                        className="shrink-0 text-[11px] text-gray-400 dark:text-grayDark-500"
                    />
                    <span className="truncate text-xs font-medium text-gray-600 dark:text-grayDark-300">
                        {docTitle || page || "index.html"}
                    </span>
                </span>
                <button
                    type="button"
                    onClick={() => setRecharge((n) => n + 1)}
                    aria-label={t("reps_preview_reload")}
                    title={t("reps_preview_reload")}
                    className="ml-auto flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:text-grayDark-400 dark:hover:bg-white/10"
                >
                    <SolarIcon name="refresh-linear" className="text-[13px]" />
                </button>
            </div>
            <LiveFrame
                html={html}
                fileMap={fileMap}
                actions={[]}
                onMessage={vue}
                title={t("reps_preview")}
                stockage={stockage}
                garder
                recharge={recharge}
            />
            {gestes && (
                <LiveFrame
                    html={html}
                    fileMap={fileMap}
                    actions={actions}
                    onMessage={controle}
                    hidden
                    stockage={stockage}
                />
            )}
            {lignes.length > 0 && (
                <div className="max-h-28 shrink-0 overflow-auto whitespace-pre-wrap border-t border-white/5 bg-[#0E161D] px-3 py-2 font-geistMono text-[12px] leading-relaxed">
                    {lignes.map((l, i) => (
                        <span
                            key={i}
                            className={l.flux === "stdout" ? "text-gray-100" : "text-red-400"}
                        >
                            {l.texte}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// Une page donnée en une seule chaîne (`data.page` des révélations) n'a pas
// toujours écrit sa balise script : on la pose en fin de page, là où un script
// trouve tous les éléments déjà construits.
function pageWithScript(page) {
    const html = String(page || "");
    return /<script\b[^>]*\bsrc\s*=/i.test(html) ? html : `${html}\n<script src="script.js"></script>`;
}

// La révélation d'une étape d'assemblage, quand le code assemblé agit sur une
// PAGE (`data.page`) : au bon verdict, on montre la page que SA ligne a changée.
function RevealPage({ code, page }) {
    const fileMap = useMemo(
        () => new Map([["index.html", pageWithScript(page)], ["script.js", String(code || "")]]),
        [code, page]
    );
    const [h, setH] = useState(96);
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="mt-3"
        >
            <LivePane
                fileMap={fileMap}
                page="index.html"
                style={{ height: Math.min(Math.max(h + 38, 120), 300) }}
                onHeight={setH}
            />
        </motion.div>
    );
}

// Un concept qui MONTRE une page vivante (`data.files` + `data.live`) : les
// fichiers en lecture seule dans leurs onglets, et la page que leurs scripts
// produisent. C'est l'équivalent, pour une page, de la console d'un concept
// `data.run`.
function ConceptLive({ data }) {
    const fileMap = useMemo(() => fileMapOf(data.files), [data.files]);
    const noms = useMemo(() => Array.from(fileMap.keys()), [fileMap]);
    const entry = useMemo(
        () => (data.entry && noms.includes(data.entry) ? data.entry : entryFileName(noms)),
        [noms, data.entry]
    );
    // L'onglet ouvert : celui que la leçon désigne (`data.open`), sinon le
    // premier script, puisque c'est lui qu'on est venu regarder.
    const [tab, setTab] = useState(
        () =>
            (data.open && noms.includes(normalizeFileName(data.open)) && normalizeFileName(data.open)) ||
            noms.find((n) => n.endsWith(".js")) ||
            noms[0]
    );
    const codeH = Math.max(...noms.map((n) => codeHeightFor(fileMap.get(n))), 96) + 36;
    const [paneH, setPaneH] = useState(codeH);
    return (
        <div className="grid gap-3 lg:grid-cols-2">
            <RepsCodeEditor
                value={fileMap.get(tab) ?? ""}
                readOnly
                language={fileLanguage(tab)}
                tabs={noms}
                activeTab={tab}
                onSelectTab={setTab}
                style={{ height: paneH }}
            />
            <div className="order-first lg:order-none">
                <LivePane
                    fileMap={fileMap}
                    page={entry}
                    stockage={data.storage}
                    style={{ height: paneH }}
                    onHeight={(h) => setPaneH(Math.min(Math.max(codeH, 38 + h), 480))}
                />
            </div>
        </div>
    );
}

/**
 * Aperçu masquable. L'état vit dans le player, pas ici : il doit survivre au
 * passage d'une étape à l'autre (l'exercice est remonté à chaque visite) et
 * repartir à zéro à la leçon suivante. Un contexte évite de le faire traverser
 * les huit composants d'exercice qui affichent un CodeStage.
 */
/**
 * Une étape a-t-elle besoin de TOUTE la largeur ? Un éditeur de code et son
 * aperçu côte à côte dans une colonne de lecture (max-w-3xl) laissent moins de
 * 370 px à chacun : le code se replie à chaque ligne. Un texte, lui, se lit
 * mal au-delà de cette largeur : on ne l'élargit donc pas.
 */
export function isWideStep(exercise) {
    if (!exercise) return false;
    const d = exercise.data || {};
    if (["write_code", "python_code", "js_code", "tune", "drop_blocks"].includes(exercise.type)) {
        return true;
    }
    if (["find_error", "predict_output", "tap_target"].includes(exercise.type)) {
        return true;
    }
    // Un QCM qui fait LIRE du code a besoin de la même largeur qu'un
    // predict_output : c'est le même écran, un bloc de code au-dessus d'options.
    if (d.code) return true;
    // Une page vivante : ses onglets et son aperçu, côte à côte.
    if (d.live) return true;
    return !!d.preview;
}

/**
 * Modèle de boîte des aperçus de l'étape courante.
 *
 * « border-box » (défaut, et le comportement de toutes les leçons écrites
 * jusqu'ici) : notre reset s'applique, une boîte à qui l'on donne 300 px en
 * mesure 300, bordure et espace dedans compris.
 * « content-box » : le reset ne s'applique PAS et l'aperçu se comporte comme un
 * vrai navigateur — les 300 px ne mesurent que le contenu, le reste s'ajoute
 * par-dessus. Sans cette option, une leçon qui enseigne le modèle de boîte
 * montrerait le contraire de ce qu'elle raconte, et `box-sizing: border-box`
 * écrit par l'apprenant ne changerait rien à l'écran.
 */
export const BoxModelContext = createContext("border-box");

export const PreviewToggleContext = createContext({
    mode: "both",
    setMode: null,
});

/**
 * Éditeur et aperçu, avec le réglage d'affichage au-dessus. Tous les exercices
 * qui montrent les deux passent par ici : sinon le réglage n'existerait que sur
 * certains d'entre eux, ce qui est exactement ce qui s'était produit.
 *
 * Trois modes : les deux, le code seul, l'aperçu seul. Sur grand écran, le
 * besoin est de replier l'aperçu pour écrire, donc un simple lien suffit. Sur
 * écran étroit, « les deux » veut dire empilés, et l'apprenant peut préférer
 * des onglets pour garder une hauteur d'écran utile : il a les trois choix.
 */
// `previewFirst` (aperçu vivant) : empilés, l'aperçu passe AU-DESSUS de
// l'éditeur. On regarde la page, puis le script qui la change. Côte à côte,
// l'ordre ne bouge pas.
function PreviewSplit({ editor, previewNode, preview, previewFirst }) {
    const t = useTranslations();
    const { mode, setMode } = useContext(PreviewToggleContext);
    if (!preview) return editor;
    if (!setMode) {
        return (
            <div className="grid gap-3 lg:grid-cols-2">
                {editor}
                {previewNode}
            </div>
        );
    }

    const showEditor = mode !== "preview";
    const showPreview = mode !== "code";
    const seg = (value, label) => (
        <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                mode === value
                    ? "bg-white text-gray-700 shadow-sm dark:bg-white/[0.12] dark:text-grayDark-100"
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-grayDark-300"
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className="space-y-2">
            <div className="flex justify-end">
                {/* Grand écran : un lien discret, l'aperçu se replie et rend
                    sa largeur à l'éditeur. */}
                <button
                    type="button"
                    onClick={() => setMode(mode === "code" ? "both" : "code")}
                    className="hidden items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 lg:flex dark:hover:bg-white/[0.06] dark:hover:text-grayDark-300"
                >
                    <SolarIcon
                        name={mode === "code" ? "eye-linear" : "eye-closed-linear"}
                        className="text-sm"
                    />
                    {mode === "code"
                        ? t("reps_preview_show")
                        : t("reps_preview_hide")}
                </button>
                {/* Écran étroit : les deux empilés, ou un seul à la fois. */}
                <div className="flex gap-0.5 rounded-lg bg-gray-100 p-0.5 lg:hidden dark:bg-white/[0.06]">
                    {seg("both", t("reps_layout_both"))}
                    {seg("code", t("reps_layout_code"))}
                    {seg("preview", t("reps_layout_preview"))}
                </div>
            </div>
            {showEditor && showPreview ? (
                // Côte à côte quand il y a vraiment la place (lg), empilés
                // sinon : deux colonnes serrées écraseraient le code.
                <div className="grid gap-3 lg:grid-cols-2">
                    {editor}
                    {previewFirst ? (
                        <div className="order-first lg:order-none">{previewNode}</div>
                    ) : (
                        previewNode
                    )}
                </div>
            ) : showEditor ? (
                editor
            ) : (
                previewNode
            )}
        </div>
    );
}

function CodeStage({ preview, html, border, toolbar, filename, children }) {
    return (
        <PreviewSplit
            preview={preview}
            editor={
                <EditorCard border={border} toolbar={toolbar} filename={filename}>
                    {children}
                </EditorCard>
            }
            previewNode={<BrowserPane html={html} />}
        />
    );
}

// Cartes de choix PARTAGÉES (mcq, predict_output) : mêmes états (sélection bleue,
// bon en vert, mauvais en rouge, autres estompés), sélection clavier 1..9.
// mono : options rendues en code (sortie de programme). notes : petites notes
// pédagogiques alignées sur les options, affichées sous l'option CHOISIE une
// fois l'exercice verrouillé (aucun effet sur la correction).
// Rendu d'une suite de jetons (lib/reps/highlight) sur fond SOMBRE : ce cadre
// reste noir quel que soit le thème de l'app, d'où des couleurs en dur plutôt
// que des classes adaptatives.
function renderTokensDark(tokens) {
    return (tokens || []).map((tk, i) =>
        tk.kind ? (
            <span key={i} style={{ color: TOKEN_DARK[tk.kind] }}>
                {tk.text}
            </span>
        ) : (
            <span key={i}>{tk.text}</span>
        )
    );
}

// Même chose sur fond CLAIR (fill_blank) : là, les classes suivent le thème.
function renderTokensLight(tokens) {
    return (tokens || []).map((tk, i) =>
        tk.kind ? (
            <span key={i} className={TOKEN_LIGHT[tk.kind]}>
                {tk.text}
            </span>
        ) : (
            <span key={i}>{tk.text}</span>
        )
    );
}

function ChoiceCards({
    options,
    notes,
    mono,
    multi,
    disabled,
    revealed,
    sel,
    correct,
    onPick,
}) {
    // Ordre d'AFFICHAGE mélangé (façon Duolingo) : la bonne réponse ne tombe jamais
    // toujours au même endroit, donc refaire une leçon force à relire plutôt qu'à
    // mémoriser une position. perm[posAffichée] = index ORIGINAL de l'option (celui
    // qu'attend la correction). 1er rendu = ordre naturel (= HTML serveur, pas de
    // mismatch d'hydratation), puis mélange au montage avant peinture. L'exercice est
    // remonté à chaque visite (key=visit) : nouveau mélange à chaque passage.
    const [perm, setPerm] = useState(() => options.map((_, i) => i));
    useLayoutEffect(() => {
        setPerm(shuffleIndices(options.length));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Selection au clavier : touches 1..9 (comme Duolingo), dans l'ordre AFFICHÉ.
    const selectRef = useRef(() => {});
    selectRef.current = (n) => {
        if (n < 1 || n > perm.length) return;
        onPick(perm[n - 1]);
    };
    useEffect(() => {
        function onKey(e) {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const ae = document.activeElement;
            if (
                ae &&
                (ae.tagName === "INPUT" ||
                    ae.tagName === "TEXTAREA" ||
                    (ae.closest && ae.closest(".monaco-editor")))
            ) {
                return;
            }
            const n = parseInt(e.key, 10);
            if (n >= 1 && n <= 9) selectRef.current(n);
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // sel et correct sont un index (mcq, predict_output) OU un tableau d'index
    // (multi_select) : on ramène les deux au même moule pour ne garder qu'un
    // seul rendu de carte.
    const selList = Array.isArray(sel) ? sel : [sel];
    const corrList = Array.isArray(correct) ? correct : [correct];

    return (
        <div className="space-y-3">
            {perm.map((oi, i) => {
                const o = options[oi];
                const isGood = revealed && corrList.includes(oi);
                const isBad = revealed && selList.includes(oi) && !corrList.includes(oi);
                const isDim = revealed && !isGood && !isBad;
                const isSel = !revealed && selList.includes(oi);
                let cls = C_NEUTRAL;
                if (isGood) cls = C_GOOD;
                else if (isBad) cls = C_BAD;
                else if (isDim) cls = C_DIM;
                else if (isSel) cls = C_SEL;
                const note =
                    revealed && selList.includes(oi) && notes && notes[oi]
                        ? notes[oi]
                        : null;
                return (
                    <button
                        key={oi}
                        type="button"
                        disabled={disabled}
                        onClick={() => onPick(oi)}
                        className={`${CARD} ${cls}`}
                    >
                        <div className="flex items-center gap-3.5">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center">
                                {multi ? (
                                    isGood || isBad || isSel ? (
                                        <SolarIcon
                                            name="check-square-bold"
                                            className={`text-[26px] ${
                                                isGood
                                                    ? "text-emerald-500"
                                                    : isBad
                                                      ? "text-red-400"
                                                      : "text-blue-500"
                                            }`}
                                        />
                                    ) : (
                                        <span
                                            className={`${BOX_EMPTY} ${
                                                isDim
                                                    ? "border-gray-200 dark:border-grayDark-700"
                                                    : "border-gray-300 dark:border-grayDark-500"
                                            }`}
                                        />
                                    )
                                ) : isGood ? (
                                    <SolarIcon
                                        name="check-circle-bold"
                                        className="text-[34px] text-emerald-500"
                                    />
                                ) : isBad ? (
                                    <SolarIcon
                                        name="close-circle-bold"
                                        className="text-[34px] text-red-400"
                                    />
                                ) : isSel ? (
                                    <SolarIcon
                                        name="check-circle-bold"
                                        className="text-[34px] text-blue-500"
                                    />
                                ) : (
                                    <span
                                        className={`flex h-9 w-9 items-center justify-center rounded-full text-[13px] font-bold ${
                                            isDim
                                                ? "bg-gray-200 text-gray-400 dark:bg-grayDark-700/60 dark:text-grayDark-600"
                                                : "bg-gray-100 text-gray-500 dark:bg-grayDark-700/50 dark:text-grayDark-400"
                                        }`}
                                    >
                                        {i + 1}
                                    </span>
                                )}
                            </span>
                            {mono ? (
                                // Sortie de programme : mono, retours à la ligne et
                                // indentation préservés, défile DANS la carte si long.
                                <span className="min-w-0 flex-1 overflow-x-auto">
                                    <span className="block w-max min-w-full whitespace-pre font-geistMono text-[13px] leading-relaxed">
                                        {o}
                                    </span>
                                </span>
                            ) : (
                                <span className="flex-1">{renderRich(o)}</span>
                            )}
                        </div>
                        {note && (
                            // span en block (pas de <p> : un <button> n'accepte
                            // que du contenu phrasé).
                            <motion.span
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.25, ease: "easeOut" }}
                                className={`mt-2 block pl-[50px] text-xs leading-relaxed ${
                                    corrList.includes(oi)
                                        ? "text-emerald-700 dark:text-emerald-300"
                                        : "text-red-600 dark:text-red-300"
                                }`}
                            >
                                {renderRich(note)}
                            </motion.span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function Mcq({ exercise, onChange, disabled, revealed, onTile, lang }) {
    const [sel, setSel] = useState(-1);
    // `data.code` : un bloc en lecture seule au-dessus des options, quand la
    // question porte sur du code qu'il faut LIRE. Sans lui, une consigne du
    // genre « ces deux paragraphes » ne désigne rien à l'écran, et l'apprenant
    // doit reconstruire de tête ce qu'on aurait dû lui montrer.
    const code = exercise.data.code;

    function pick(i) {
        if (disabled) return;
        setSel(i);
        onChange(i, true);
        if (onTile) onTile();
    }

    const choix = (
        <ChoiceCards
            options={exercise.data.options || []}
            notes={exercise.data.optionNotes}
            disabled={disabled}
            revealed={revealed}
            sel={sel}
            correct={exercise.solution.correctIndex}
            onPick={pick}
        />
    );

    if (!code) return choix;
    return (
        <div className="space-y-4">
            <RepsCodeEditor
                value={code}
                readOnly
                language={codeLang(code, exercise.data.language, lang)}
                filename={panelFileName(
                    exercise.data,
                    codeLang(code, exercise.data.language, lang)
                )}
                style={{ height: codeHeightFor(code) }}
            />
            {choix}
        </div>
    );
}

// CHOIX MULTIPLES : même carte que le mcq, mais on coche autant de propositions
// qu'on veut. À utiliser quand plusieurs réponses se défendent : un mcq
// obligerait alors à en déclarer une seule juste, et l'exercice serait injuste.
// Correction : l'ENSEMBLE coché doit être exactement l'ensemble attendu.
function MultiSelect({ exercise, onChange, disabled, revealed, onTile, lang }) {
    const t = useTranslations();
    const [sel, setSel] = useState([]);
    // `data.code` : même extension que le mcq. Un tri (« qu'est-ce qui manque à
    // cette page ? ») ne se fait pas de mémoire : sans le code sous les yeux,
    // l'apprenant doit reconstruire la page qu'on lui décrit.
    const code = exercise.data.code;

    function toggle(i) {
        if (disabled) return;
        const next = sel.includes(i) ? sel.filter((x) => x !== i) : [...sel, i];
        setSel(next);
        // Complet dès la première case cochée : « ne rien cocher » n'est jamais
        // la réponse, donc le bouton Vérifier resterait éteint pour rien.
        onChange(next, next.length > 0);
        if (onTile) onTile();
    }

    return (
        <div className={code ? "space-y-4" : undefined}>
            {code ? (
                <RepsCodeEditor
                    value={code}
                    readOnly
                    language={codeLang(code, exercise.data.language, lang)}
                    filename={panelFileName(
                        exercise.data,
                        codeLang(code, exercise.data.language, lang)
                    )}
                    style={{ height: codeHeightFor(code) }}
                />
            ) : null}
            <div>
                <p className="mb-3 text-xs text-gray-500 dark:text-grayDark-400">
                    {t("reps_multi_select_instruction")}
                </p>
                <ChoiceCards
                    multi
                    options={exercise.data.options || []}
                    notes={exercise.data.optionNotes}
                    disabled={disabled}
                    revealed={revealed}
                    sel={sel}
                    correct={exercise.solution.correctIndexes || []}
                    onPick={toggle}
                />
            </div>
        </div>
    );
}

// PRÉDIS LA SORTIE : un bloc de code en lecture seule (même rendu que ConceptCode
// sans aperçu) au-dessus d'options façon mcq rendues en mono. L'apprenant lit le
// code et choisit ce qu'il affiche. Correction : index de l'option choisie.
function PredictOutput({ exercise, onChange, disabled, revealed, onTile, lang }) {
    const [sel, setSel] = useState(-1);
    const d = exercise.data || {};
    const code = d.code || "";
    const language = codeLang(code, d.language, lang);
    const codeH = codeHeightFor(code);

    function pick(i) {
        if (disabled) return;
        setSel(i);
        onChange(i, true);
        if (onTile) onTile();
    }

    return (
        <div className="space-y-4">
            <RepsCodeEditor
                value={code}
                readOnly
                language={language}
                filename={panelFileName(d, language)}
                style={{ height: codeH }}
            />
            <ChoiceCards
                options={d.options || []}
                mono
                disabled={disabled}
                revealed={revealed}
                sel={sel}
                correct={(exercise.solution || {}).correctIndex}
                onPick={pick}
            />
        </div>
    );
}

// TROUVE L'ERREUR : le code est rendu ligne par ligne dans un éditeur sombre
// (numéros de ligne, indentation préservée) ; l'apprenant touche la ligne
// fautive (une seule sélection). À la révélation : la ligne correcte en vert
// pâle, la sélection fausse en rouge. Les lignes longues défilent DANS
// l'éditeur (overflow-x-auto), jamais de débordement de page.
function FindError({ exercise, onChange, disabled, revealed, onTile, lang }) {
    const t = useTranslations();
    const d = exercise.data || {};
    const code = d.code || "";
    const lines = code.split("\n");
    const language = codeLang(code, d.language, lang);
    // Coloration calculée sur le code ENTIER, puis redistribuée ligne par
    // ligne : une ligne isolée perdrait son contexte (une accolade ouverte
    // plus haut, une chaîne à cheval).
    const lineTokens = tokenizeLines(code, language);
    const correct = (exercise.solution || {}).lineIndex;
    const [sel, setSel] = useState(-1);
    const active = !disabled && !revealed;

    function pick(i) {
        if (!active) return;
        setSel(i);
        onChange(i, true);
        if (onTile) onTile();
    }

    return (
        <div>
            <p className="mb-3 text-xs text-gray-500 dark:text-grayDark-400">
                {t("reps_find_error_instruction")}
            </p>
            <div className="flex flex-col overflow-hidden rounded-xl border border-[#2a2a2a] bg-[#1e1e1e]">
                <div className="flex shrink-0 items-center gap-2 border-b border-[#333] bg-[#252526] px-3 py-2">
                    <span className="h-2.5 w-2.5 rounded-sm bg-orange-500" />
                    <span className="font-geistMono text-xs text-gray-300">
                        {panelFileName(d, language)}
                    </span>
                </div>
                <div className="overflow-x-auto py-2">
                    {/* w-max + min-w-full : le fond des lignes couvre toute la
                        largeur défilable, même quand une ligne dépasse. */}
                    <div className="flex w-max min-w-full flex-col font-geistMono text-[13px] leading-[1.9] text-[#d4d4d4]">
                        {lines.map((ln, i) => {
                            const isGood = revealed && i === correct;
                            const isBad = revealed && i === sel && i !== correct;
                            const isSel = !revealed && i === sel;
                            // Le fond de la ligne reste NEUTRE : les balises sont
                            // déjà bleues (TOKEN_DARK.tag), un voile bleu par-dessus
                            // rendait la ligne choisie illisible. L'état se lit sur
                            // le liseré de gauche, qui ne recouvre aucun caractère.
                            let cls = "";
                            let bar = "bg-transparent";
                            if (isGood) {
                                cls = "bg-emerald-500/15";
                                bar = "bg-emerald-400";
                            } else if (isBad) {
                                cls = "bg-red-500/15";
                                bar = "bg-red-400";
                            } else if (isSel) {
                                cls = "bg-white/[0.08]";
                                bar = "bg-sky-400";
                            } else if (active) cls = "hover:bg-white/[0.06]";
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    disabled={!active}
                                    onClick={() => pick(i)}
                                    className={`flex w-full min-w-max items-stretch text-left transition-colors duration-150 ${cls} ${
                                        active ? "cursor-pointer" : "cursor-default"
                                    }`}
                                >
                                    <span
                                        className={`w-[3px] shrink-0 ${bar}`}
                                        aria-hidden="true"
                                    />
                                    <span
                                        className={`w-9 shrink-0 select-none pr-3 text-right ${
                                            isGood
                                                ? "text-emerald-400"
                                                : isBad
                                                  ? "text-red-400"
                                                  : isSel
                                                    ? "text-gray-300"
                                                    : "text-[#6e7681]"
                                        }`}
                                    >
                                        {i + 1}
                                    </span>
                                    <span className="whitespace-pre border-l border-[#2a2a2a] pl-3 pr-4">
                                        {ln
                                            ? renderTokensDark(lineTokens[i])
                                            : " "}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

// TOUCHE LA CIBLE (façon Duolingo « tap the word ») : le code est découpé en
// segments (data.parts, concaténés à l'affichage dans un éditeur sombre) et
// l'apprenant TOUCHE le segment que demande la consigne (« Touche la balise qui
// referme le paragraphe »). Un segment fait de blancs/retours est inerte (les
// "\n" servent de séparateurs de mise en page). Réponse = index RÉEL du segment
// touché — jamais mélangé : la position dans le code EST le sens de l'exercice.
// solution.index = cible unique ; solution.accept = plusieurs cibles valides.
function TapTarget({ exercise, onChange, disabled, revealed, onTile, lang }) {
    const t = useTranslations();
    const d = exercise.data || {};
    const parts = d.parts || [];
    const language = codeLang(parts.join(""), d.language, lang);
    // Idem : « font-size » seul n'a plus de deux-points derrière lui, donc on
    // colorie la concaténation puis on rend son morceau à chaque segment.
    const partTokens = tokenizeParts(parts, language);
    const sol = exercise.solution || {};
    const good = new Set(
        Array.isArray(sol.accept) && sol.accept.length
            ? sol.accept
            : sol.index !== undefined
              ? [sol.index]
              : []
    );
    const [sel, setSel] = useState(-1);
    const active = !disabled && !revealed;

    function pick(i) {
        if (!active) return;
        setSel(i);
        onChange(i, true);
        if (onTile) onTile();
    }

    return (
        <div>
            <p className="mb-3 text-xs text-gray-500 dark:text-grayDark-400">
                {t("reps_tap_target_instruction")}
            </p>
            <div className="flex flex-col overflow-hidden rounded-xl border border-[#2a2a2a] bg-[#1e1e1e]">
                <div className="flex shrink-0 items-center gap-2 border-b border-[#333] bg-[#252526] px-3 py-2">
                    <span className="h-2.5 w-2.5 rounded-sm bg-orange-500" />
                    <span className="font-geistMono text-xs text-gray-300">
                        {panelFileName(d, language)}
                    </span>
                </div>
                <div className="overflow-x-auto px-4 py-3">
                    <div className="whitespace-pre-wrap font-geistMono text-[13.5px] leading-[2.1] text-[#d4d4d4]">
                        {parts.map((p, i) => {
                            // Séparateur (blancs/retours) : inerte, jamais touchable.
                            if (!String(p).trim()) {
                                return <span key={i}>{p}</span>;
                            }
                            const isGood = revealed && good.has(i);
                            const isBad = revealed && i === sel && !good.has(i);
                            const isSel = !revealed && i === sel;
                            let cls =
                                "decoration-dotted underline decoration-[#6e7681] underline-offset-4";
                            if (isGood)
                                cls =
                                    "bg-emerald-500/20 text-emerald-200 no-underline";
                            else if (isBad)
                                cls = "bg-red-500/20 text-red-300 no-underline";
                            else if (isSel)
                                cls =
                                    "bg-blue-500/25 no-underline ring-1 ring-blue-400/50";
                            else if (active)
                                cls += " hover:bg-white/[0.08]";
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    disabled={!active}
                                    onClick={() => pick(i)}
                                    className={`inline whitespace-pre-wrap rounded-md px-0.5 py-1 text-left font-geistMono transition-colors duration-150 ${cls} ${
                                        active
                                            ? "cursor-pointer"
                                            : "cursor-default"
                                    }`}
                                >
                                    {renderTokensDark(partTokens[i])}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function TypeAnswer({ exercise, onChange, disabled, revealed, verdict }) {
    const [val, setVal] = useState("");
    const accepted = exercise.solution.accepted || [];
    // Reponse de CODE -> on montre l'editeur (index.html) + apercu live, pas un input.
    const isCode =
        /[<>]/.test(exercise.data.placeholder || "") ||
        accepted.some((a) => /[<>]/.test(a));
    const preview = exercise.data.preview !== false;

    function applyVal(v) {
        setVal(v);
        onChange(v, v.trim().length > 0);
    }

    return (
        <div>
            {isCode ? (
                preview ? (
                    <PreviewSplit
                        preview
                        editor={
                            <RepsCodeEditor
                                value={val}
                                onChange={applyVal}
                                disabled={disabled}
                                className="h-[180px] sm:h-[220px]"
                            />
                        }
                        previewNode={
                            <BrowserPane
                                html={val}
                                className="h-[180px] sm:h-[220px]"
                            />
                        }
                    />
                ) : (
                    <RepsCodeEditor
                        value={val}
                        onChange={applyVal}
                        disabled={disabled}
                        className="h-[180px] sm:h-[220px]"
                    />
                )
            ) : (
                <input
                    type="text"
                    value={val}
                    disabled={disabled}
                    placeholder={exercise.data.placeholder || ""}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(e) => applyVal(e.target.value)}
                    className={`w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-900 outline-hidden placeholder:text-gray-400 focus:border-[#8b7ff0] dark:bg-white/[0.04] dark:text-white dark:placeholder:text-grayDark-500 ${
                        verdict === true
                            ? "border-green-500"
                            : verdict === false
                              ? "border-red-500"
                              : "border-gray-300 dark:border-white/15"
                    }`}
                />
            )}
            {exercise.data.hint && !revealed && (
                <p className="mt-2 text-xs text-gray-500">{exercise.data.hint}</p>
            )}
        </div>
    );
}

function FillBlank({ exercise, uid, onChange, disabled, revealed, verdict, onTile, lang }) {
    const FLY = useFly();
    const reduce = useReducedMotion();
    const gid = uid || exercise.slug || "x";
    const parts = (exercise.data.template || "").split("___");
    // Le gabarit troué reste du code : on le colorie d'un bloc (les trous
    // comptent comme du texte ordinaire) et chaque morceau reçoit sa part.
    const language = codeLang(
        exercise.data.template || "",
        exercise.data.language,
        lang
    );
    const partTokens = tokenizeParts(parts, language);
    const chips = exercise.data.chips || [];
    const sol = exercise.solution.blanks || [];
    const nBlanks = parts.length - 1;
    const [vals, setVals] = useState(() => Array(nBlanks).fill(null));
    const [chipForBlank, setChipForBlank] = useState(() => Array(nBlanks).fill(null));
    const [usedChip, setUsedChip] = useState(() => Array(chips.length).fill(false));

    function report(next) {
        onChange(next, next.every((v) => v != null));
    }
    function fill(ci) {
        if (disabled || usedChip[ci]) return;
        const bi = vals.findIndex((v) => v == null);
        if (bi < 0) return;
        const nv = [...vals];
        nv[bi] = chips[ci];
        const nf = [...chipForBlank];
        nf[bi] = ci;
        const nu = [...usedChip];
        nu[ci] = true;
        setVals(nv);
        setChipForBlank(nf);
        setUsedChip(nu);
        report(nv);
        if (onTile) onTile();
    }
    function clear(bi) {
        if (disabled || vals[bi] == null) return;
        const ci = chipForBlank[bi];
        const nv = [...vals];
        nv[bi] = null;
        const nf = [...chipForBlank];
        nf[bi] = null;
        const nu = [...usedChip];
        if (ci != null) nu[ci] = false;
        setVals(nv);
        setChipForBlank(nf);
        setUsedChip(nu);
        report(nv);
    }

    return (
        <LayoutGroup id={`fb-${gid}`}>
            <div>
                <CodeStage
                    preview={exercise.data.preview}
                    html={buildHtml(parts, vals)}
                    filename={panelFileName(exercise.data, language)}
                >
                    <div>
                        {parts.map((seg, i) => (
                            <span key={i}>
                                <span style={{ whiteSpace: "pre-wrap" }}>
                                    {renderTokensLight(partTokens[i])}
                                </span>
                                {i < nBlanks && (
                                    <motion.button
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => clear(i)}
                                        animate={
                                            !reduce && vals[i] != null && !revealed
                                                ? { scale: [1, 1.08, 1] }
                                                : {}
                                        }
                                        transition={{ duration: 0.22 }}
                                        className={`mx-1 inline-flex min-w-[64px] items-center justify-center rounded-md border px-2 py-0.5 text-center align-middle text-[13px] ${
                                            revealed
                                                ? vals[i] === sol[i]
                                                    ? "border-green-500 bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300"
                                                    : "border-red-500 bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                                                : vals[i] != null
                                                  ? "border-[#8b7ff0] bg-[#8b7ff0]/10 text-[#5b4fd0] dark:bg-[#8b7ff0]/20 dark:text-white"
                                                  : "border-dashed border-gray-300 text-gray-400 dark:border-white/25 dark:text-grayDark-500"
                                        }`}
                                    >
                                        {/* Verdict tombé : plus AUCUN layoutId.
                                            L'étiquette n'a plus à voler, et la
                                            garder appariée laissait le FLIP la
                                            poser à côté de son emplacement
                                            (constaté sur le commentaire, où la
                                            réponse s'affichait au-dessus du
                                            cadre). */}
                                        {revealed ? (
                                            <span>{vals[i] || " "}</span>
                                        ) : vals[i] != null ? (
                                            <motion.span
                                                layoutId={`fbk-${gid}-${chipForBlank[i]}`}
                                                layout
                                                transition={FLY}
                                            >
                                                {vals[i]}
                                            </motion.span>
                                        ) : (
                                            <span className="opacity-0">x</span>
                                        )}
                                    </motion.button>
                                )}
                            </span>
                        ))}
                    </div>
                </CodeStage>

                {/* Révélation : le gabarit complété, exécuté, au bon verdict. */}
                {verdict === true && exercise.data.reveal && executableLang(lang) && (
                    <RevealConsole
                    page={exercise.data.page}
                        code={buildHtml(parts, vals)}
                        inputs={exercise.data.inputs}
                        filename={panelFileName(exercise.data, lang)}
                        language={lang}
                    />
                )}

                <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {chips.map((c, ci) =>
                        usedChip[ci] ? (
                            <span
                                key={ci}
                                aria-hidden
                                className={`${CHIP} invisible`}
                            >
                                {c}
                            </span>
                        ) : (
                            <motion.button
                                key={ci}
                                layoutId={`fbk-${gid}-${ci}`}
                                layout
                                type="button"
                                disabled={disabled}
                                onClick={() => fill(ci)}
                                whileTap={reduce ? undefined : { scale: 0.88 }}
                                transition={FLY}
                                className={CHIP}
                            >
                                {c}
                            </motion.button>
                        )
                    )}
                </div>
            </div>
        </LayoutGroup>
    );
}

// Indentation par bloc : une balise fermante dé-indente avant, une ouvrante indente après.
function tokenIndents(seq) {
    const out = [];
    let depth = 0;
    for (const s of seq) {
        if (isCloseTag(s.tok)) depth = Math.max(0, depth - 1);
        out.push(depth);
        if (isOpenTag(s.tok)) depth++;
    }
    return out;
}

// Builder de code par blocs. On TAPE un bloc pour l'ajouter à la fin, ou on le
// GLISSE pour l'insérer / réordonner / remplacer n'importe où (indentation auto).
// Drag maison en pointer events (souris + tactile), cohérent avec ReorderExercise.
function TapTokens({ exercise, uid, onChange, disabled, revealed, verdict, onTile, lang }) {
    const t = useTranslations();
    const tokens = exercise.data.tokens || [];
    // Ordre d'AFFICHAGE mélangé de la banque (jamais dans l'ordre de la réponse) :
    // impossible de recopier la séquence à l'aveugle. Les bankIdx restent les index
    // RÉELS (correction et positions inchangées), seul l'affichage change. 1er rendu
    // = ordre naturel (pas de mismatch d'hydratation), mélange au montage.
    const [tokOrder, setTokOrder] = useState(() => tokens.map((_, i) => i));
    useLayoutEffect(() => {
        setTokOrder(shuffleIndices(tokens.length));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [seq, setSeq] = useState([]); // [{ tok, bankIdx }]
    const [drag, setDrag] = useState(null); // { kind:'bank'|'placed', bankIdx?, fromPos?, tok, x, y, moved }
    const [drop, setDrop] = useState(null); // { mode:'insert'|'replace'|'remove', index }
    const editorRef = useRef(null);
    const rowEls = useRef({}); // bankIdx -> élément (positions courantes)
    const startRef = useRef({ x: 0, y: 0 });
    const dragRef = useRef(null);

    const active = !disabled && !revealed;
    const used = tokens.map((_, ci) => seq.some((s) => s.bankIdx === ci));
    // Mode EN LIGNE : Python et contenus sans balises (phrases, code d'une ligne).
    // Les jetons coulent côte à côte comme du vrai code ; l'empilement une-balise-
    // par-ligne avec indentation n'a de sens que pour du HTML imbriqué.
    const inline = executableLang(lang) !== null || !tokens.some(isTag);
    const indents = inline ? seq.map(() => 0) : tokenIndents(seq);
    const builtHtml = seq.map((s) => s.tok).join("");

    function apply(next) {
        setSeq(next);
        onChange(
            next.map((s) => s.tok),
            next.length > 0,
        );
    }
    function append(ci) {
        apply([...seq, { tok: tokens[ci], bankIdx: ci }]);
        if (onTile) onTile();
    }
    function insertAt(ci, gap) {
        apply([
            ...seq.slice(0, gap),
            { tok: tokens[ci], bankIdx: ci },
            ...seq.slice(gap),
        ]);
        if (onTile) onTile();
    }
    function replaceAt(pos, ci) {
        apply(
            seq.map((s, i) => (i === pos ? { tok: tokens[ci], bankIdx: ci } : s)),
        );
        if (onTile) onTile();
    }
    function removeAt(pos) {
        apply(seq.filter((_, i) => i !== pos));
    }
    function moveTo(fromPos, gap) {
        if (gap === fromPos || gap === fromPos + 1) return;
        const item = seq[fromPos];
        const without = seq.filter((_, i) => i !== fromPos);
        const idx = gap > fromPos ? gap - 1 : gap;
        apply([...without.slice(0, idx), item, ...without.slice(idx)]);
        if (onTile) onTile();
    }
    function reset() {
        if (active) apply([]);
    }
    function undo() {
        if (active && seq.length) removeAt(seq.length - 1);
    }

    // Cible de dépôt : remplacement (sur le tiers central d'un bloc, drag depuis la
    // banque), insertion (sinon, au gap le plus proche), ou retrait (bloc lâché hors zone).
    function computeDrop(px, py, d) {
        const ed = editorRef.current;
        if (!ed) return null;
        const er = ed.getBoundingClientRect();
        const inside =
            px >= er.left - 32 &&
            px <= er.right + 32 &&
            py >= er.top - 18 &&
            py <= er.bottom + 18;
        if (!inside) return d.kind === "placed" ? { mode: "remove" } : null;
        if (seq.length === 0) return { mode: "insert", index: 0 };
        if (inline) {
            // Flux horizontal (avec retours à la ligne possibles) : on vise le
            // jeton le plus proche, remplacement sur son tiers central, insertion
            // avant/après selon le côté.
            let best = null;
            for (let pos = 0; pos < seq.length; pos++) {
                const el = rowEls.current[seq[pos].bankIdx];
                if (!el) continue;
                const r = el.getBoundingClientRect();
                const dist = Math.hypot(
                    px - (r.left + r.right) / 2,
                    py - (r.top + r.bottom) / 2
                );
                if (!best || dist < best.dist) best = { pos, r, dist };
            }
            if (!best) return { mode: "insert", index: seq.length };
            const { pos, r } = best;
            const third = r.width / 3;
            if (
                d.kind === "bank" &&
                px >= r.left + third &&
                px <= r.right - third &&
                py >= r.top &&
                py <= r.bottom
            ) {
                return { mode: "replace", index: pos };
            }
            return {
                mode: "insert",
                index: px < (r.left + r.right) / 2 ? pos : pos + 1,
            };
        }
        for (let pos = 0; pos < seq.length; pos++) {
            const el = rowEls.current[seq[pos].bankIdx];
            if (!el) continue;
            const r = el.getBoundingClientRect();
            if (py < r.top || py > r.bottom) continue;
            const third = r.height / 3;
            if (d.kind === "bank" && py >= r.top + third && py <= r.bottom - third) {
                return { mode: "replace", index: pos };
            }
            return {
                mode: "insert",
                index: py < r.top + r.height / 2 ? pos : pos + 1,
            };
        }
        const first = rowEls.current[seq[0].bankIdx];
        if (first && py < first.getBoundingClientRect().top) {
            return { mode: "insert", index: 0 };
        }
        return { mode: "insert", index: seq.length };
    }

    function begin(e, d) {
        if (!active || dragRef.current) return;
        startRef.current = { x: e.clientX, y: e.clientY };
        const nd = { ...d, x: e.clientX, y: e.clientY, moved: false };
        dragRef.current = nd;
        setDrag(nd);
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch (err) {
            // ignore
        }
    }
    function onMove(e) {
        const d = dragRef.current;
        if (!d) return;
        const moved =
            d.moved ||
            Math.abs(e.clientX - startRef.current.x) > 5 ||
            Math.abs(e.clientY - startRef.current.y) > 5;
        const nd = { ...d, x: e.clientX, y: e.clientY, moved };
        dragRef.current = nd;
        setDrag(nd);
        setDrop(moved ? computeDrop(e.clientX, e.clientY, nd) : null);
    }
    function onUp(e) {
        const d = dragRef.current;
        dragRef.current = null;
        setDrag(null);
        setDrop(null);
        if (!d) return;
        if (!d.moved) {
            // Simple tap : ajout (banque) ou retrait (bloc placé).
            if (d.kind === "bank") append(d.bankIdx);
            else removeAt(d.fromPos);
            return;
        }
        const dp = computeDrop(e.clientX, e.clientY, d);
        if (d.kind === "bank") {
            if (dp && dp.mode === "replace") replaceAt(dp.index, d.bankIdx);
            else if (dp && dp.mode === "insert") insertAt(d.bankIdx, dp.index);
        } else {
            if (dp && dp.mode === "remove") removeAt(d.fromPos);
            else if (dp && dp.mode === "insert") moveTo(d.fromPos, dp.index);
        }
    }
    function onCancel() {
        // Geste interrompu (annulation système, multi-touch…) : on abandonne sans rien poser.
        dragRef.current = null;
        setDrag(null);
        setDrop(null);
    }

    const marker = (gap) => {
        const on =
            drag && drag.moved && drop && drop.mode === "insert" && drop.index === gap;
        // En ligne : barre VERTICALE entre deux jetons ; en pile : barre horizontale.
        if (inline) {
            return (
                <span
                    className={`inline-block w-[3px] self-stretch rounded-full transition-colors ${
                        on ? "bg-primary" : "bg-transparent"
                    }`}
                />
            );
        }
        return (
            <div
                className={`mx-1 h-1 rounded-full transition-colors ${
                    on ? "bg-primary" : "bg-transparent"
                }`}
            />
        );
    };

    const toolbar = (
        <>
            <button type="button" onClick={reset} aria-label={t("reps_clear_all")}>
                <SolarIcon name="restart-linear" />
            </button>
            <button type="button" onClick={undo} aria-label={t("reps_undo")}>
                <SolarIcon name="backspace-linear" />
            </button>
        </>
    );

    return (
        <div>
            <CodeStage
                preview={exercise.data.preview}
                html={builtHtml}
                toolbar={toolbar}
                filename={panelFileName(
                    exercise.data,
                    codeLang(builtHtml, exercise.data.language, lang)
                )}
            >
                <div
                    ref={editorRef}
                    className={`min-h-[2.5rem] py-0.5 ${
                        inline
                            ? "flex flex-wrap items-stretch gap-y-1.5"
                            : "flex flex-col"
                    }`}
                    style={{ touchAction: active ? "none" : "auto" }}
                >
                    {marker(0)}
                    {seq.length === 0 && (
                        <p className="px-1 py-1.5 text-[13px] text-gray-400">
                            {t("reps_blocks_to_place")}
                        </p>
                    )}
                    {seq.map((s, pos) => {
                        const isDragged =
                            drag &&
                            drag.kind === "placed" &&
                            drag.fromPos === pos &&
                            drag.moved;
                        const isReplace =
                            drag &&
                            drag.moved &&
                            drop &&
                            drop.mode === "replace" &&
                            drop.index === pos;
                        return (
                            // display:contents en ligne : le jeton et son marqueur
                            // participent directement au flux flex du conteneur.
                            <div key={s.bankIdx} className={inline ? "contents" : ""}>
                                <div
                                    ref={(el) => {
                                        if (el) rowEls.current[s.bankIdx] = el;
                                        else delete rowEls.current[s.bankIdx];
                                    }}
                                    onPointerDown={(e) =>
                                        begin(e, {
                                            kind: "placed",
                                            fromPos: pos,
                                            tok: s.tok,
                                        })
                                    }
                                    onPointerMove={onMove}
                                    onPointerUp={onUp}
                                    onPointerCancel={onCancel}
                                    style={{
                                        paddingLeft: inline
                                            ? undefined
                                            : `${indents[pos] * 1.25 + 0.25}rem`,
                                        touchAction: active ? "none" : "auto",
                                        opacity: isDragged ? 0.35 : 1,
                                    }}
                                    className={`select-none ${
                                        inline
                                            ? "inline-flex items-center px-px py-[3px]"
                                            : "flex w-full py-[3px]"
                                    } ${
                                        active
                                            ? "cursor-grab active:cursor-grabbing"
                                            : "cursor-default"
                                    }`}
                                >
                                    <span
                                        className={`rounded px-1.5 py-0.5 font-geistMono text-[13px] ${
                                            isTag(s.tok)
                                                ? "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300"
                                                : "bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-grayDark-100"
                                        } ${isReplace ? "ring-2 ring-primary" : ""}`}
                                    >
                                        {s.tok}
                                    </span>
                                </div>
                                {marker(pos + 1)}
                            </div>
                        );
                    })}
                </div>
            </CodeStage>

            {/* Révélation : la ligne assemblée, exécutée, au bon verdict. */}
            {verdict === true && exercise.data.reveal && executableLang(lang) && (
                <RevealConsole
                    page={exercise.data.page}
                    code={builtHtml}
                    inputs={exercise.data.inputs}
                    filename={panelFileName(exercise.data, lang)}
                    language={lang}
                />
            )}

            <div className="mt-4 flex flex-wrap justify-center gap-2">
                {tokOrder.map((ci) => {
                    const tok = tokens[ci];
                    return used[ci] ? (
                        <span key={ci} aria-hidden className={`${CHIP} invisible`}>
                            {tok}
                        </span>
                    ) : (
                        <button
                            key={ci}
                            type="button"
                            disabled={!active}
                            onPointerDown={(e) =>
                                begin(e, { kind: "bank", bankIdx: ci, tok })
                            }
                            onPointerMove={onMove}
                            onPointerUp={onUp}
                            onPointerCancel={onCancel}
                            style={{
                                touchAction: active ? "none" : "auto",
                                opacity:
                                    drag &&
                                    drag.kind === "bank" &&
                                    drag.bankIdx === ci &&
                                    drag.moved
                                        ? 0.3
                                        : 1,
                            }}
                            className={`${CHIP} ${
                                active ? "cursor-grab active:cursor-grabbing" : ""
                            }`}
                        >
                            {tok}
                        </button>
                    );
                })}
            </div>

            {drag && drag.moved && (
                <div
                    className="pointer-events-none fixed z-[100] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-gray-300 bg-white px-3 py-2 font-geistMono text-[13px] text-gray-800 shadow-xl dark:border-white/20 dark:bg-[#253340] dark:text-grayDark-100"
                    style={{ left: drag.x, top: drag.y }}
                >
                    {drag.tok}
                </div>
            )}
        </div>
    );
}

// Glisser-deposer maison en pointer events (souris + tactile) : l'item suit le doigt,
// les autres se replacent en douceur (layout), surbrillance claire de l'item saisi.
function ReorderExercise({ exercise, onChange, disabled, revealed, verdict, lang }) {
    const t = useTranslations();
    const items = exercise.data.items || [];
    // Mode CODE (data.code: true) : items rendus en mono, indentation préservée
    // (indispensable en Python). Aucun changement de correction.
    const isCode = !!exercise.data.code;
    // Aperçu LIVE (data.preview: true, items = lignes de HTML) : la page se
    // recompose sous la liste au fil du réordonnancement — on VOIT la page
    // cassée se réparer. Aucun changement de correction.
    const preview = !!exercise.data.preview;
    const sol = exercise.solution.order || [];
    const [order, setOrder] = useState(() => items.map((_, i) => i));
    const [dragItem, setDragItem] = useState(null);
    const [dy, setDy] = useState(0);
    const startY = useRef(0);
    const rowEls = useRef({});

    useLayoutEffect(() => {
        // Affichage mélangé (jamais l'ordre déjà résolu) : l'apprenant doit vraiment
        // ranger, pas juste valider. 1er rendu = ordre naturel (pas de mismatch
        // d'hydratation), mélange au montage.
        let o = items.map((_, i) => i);
        const solStr = JSON.stringify(sol);
        for (let a = 0; a < 40; a++) {
            const cand = shuffleIndices(items.length);
            if (JSON.stringify(cand) !== solStr) {
                o = cand;
                break;
            }
        }
        setOrder(o);
        // L'ordre affiche est GARANTI different de la solution (boucle
        // ci-dessus) : il faut donc au moins un deplacement, « Verifier »
        // reste inactif jusque-la. Sans ce garde-fou, une touche Entree de
        // trop validait l'ordre melange = echec certain. Cas degenere (aucun
        // melange possible, ex. un seul item) : on laisse validable pour ne
        // jamais bloquer la lecon.
        onChange(o, JSON.stringify(o) === solStr);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const active = !disabled && !revealed;

    function begin(e, itemIdx) {
        if (!active) return;
        setDragItem(itemIdx);
        setDy(0);
        startY.current = e.clientY;
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch (err) {
            // ignore
        }
    }
    function move(e) {
        if (dragItem == null) return;
        const pointerY = e.clientY;
        setDy(pointerY - startY.current);
        const fromPos = order.indexOf(dragItem);
        let toPos = fromPos;
        for (let pos = 0; pos < order.length; pos++) {
            if (pos === fromPos) continue;
            const el = rowEls.current[order[pos]];
            if (!el) continue;
            const r = el.getBoundingClientRect();
            const mid = r.top + r.height / 2;
            if (pos < fromPos && pointerY < mid) toPos = Math.min(toPos, pos);
            else if (pos > fromPos && pointerY > mid) toPos = Math.max(toPos, pos);
        }
        if (toPos !== fromPos) {
            const next = [...order];
            next.splice(fromPos, 1);
            next.splice(toPos, 0, dragItem);
            setOrder(next);
            onChange(next, true);
            startY.current = pointerY;
            setDy(0);
        }
    }
    function end() {
        setDragItem(null);
        setDy(0);
    }

    return (
        <div>
            <p className="mb-3 text-xs text-gray-500">
                {t("reps_reorder_instruction")}
            </p>
            <div className="flex flex-col gap-2.5">
                {order.map((itemIdx, pos) => {
                    const dragging = dragItem === itemIdx;
                    let cls;
                    if (revealed) cls = sol[pos] === itemIdx ? C_GOOD : C_BAD;
                    else if (dragging)
                        cls =
                            "border-[#8b7ff0] bg-white shadow-lg ring-2 ring-[#8b7ff0]/40 dark:bg-[#253340]";
                    else cls = C_NEUTRAL;
                    return (
                        <motion.div
                            key={itemIdx}
                            ref={(el) => {
                                rowEls.current[itemIdx] = el;
                            }}
                            layout={!dragging}
                            transition={{ type: "spring", stiffness: 600, damping: 40 }}
                            onPointerDown={(e) => begin(e, itemIdx)}
                            onPointerMove={move}
                            onPointerUp={end}
                            onPointerCancel={end}
                            style={
                                dragging
                                    ? {
                                          transform: `translateY(${dy}px)`,
                                          zIndex: 50,
                                          position: "relative",
                                          touchAction: "none",
                                      }
                                    : { touchAction: active ? "none" : "auto" }
                            }
                            className={`flex select-none items-center gap-3 rounded-xl border p-3.5 text-sm transition-colors ${cls} ${
                                active ? "cursor-grab active:cursor-grabbing" : "cursor-default"
                            }`}
                        >
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#8b7ff0] text-xs text-white">
                                {pos + 1}
                            </span>
                            {isCode ? (
                                // Ligne de code : mono + whitespace-pre, défile
                                // dans la carte si elle est longue (min-w-0 requis
                                // pour ne pas faire déborder le flex).
                                <span className="min-w-0 flex-1 overflow-x-auto">
                                    <span className="block w-max min-w-full whitespace-pre font-geistMono text-[13px] leading-relaxed">
                                        {items[itemIdx]}
                                    </span>
                                </span>
                            ) : (
                                <span className="min-w-0 flex-1">
                                    {renderRich(items[itemIdx])}
                                </span>
                            )}
                            {active && (
                                <SolarIcon
                                    name="hamburger-menu-linear"
                                    className="shrink-0 text-gray-400"
                                />
                            )}
                        </motion.div>
                    );
                })}
            </div>
            {preview && (
                <BrowserPane
                    html={order.map((i) => items[i] ?? "").join("\n")}
                    className="mt-3"
                    style={{ height: 180 }}
                />
            )}
            {/* Révélation : le programme remis dans l'ordre, exécuté, au bon verdict. */}
            {verdict === true && exercise.data.reveal && executableLang(lang) && (
                <RevealConsole
                    page={exercise.data.page}
                    code={order.map((i) => items[i] ?? "").join("\n")}
                    filename={fileNameFor(lang)}
                    language={lang}
                />
            )}
        </div>
    );
}

// Coloration "editeur sombre" (style VS Code) : balises bleutees, texte clair.

// GLISSER DES BLOCS DANS L'EDITEUR : un vrai editeur de code sombre (en-tete index.html,
// numeros de ligne) avec des EMPLACEMENTS vides dans le code ; on glisse des blocs depuis
// une palette pour les y deposer, avec apercu live. Le template porte des marqueurs {0},
// {1}... pour les emplacements. Drag au pointeur (desktop + tactile) ; clic sur un bloc
// place = le retirer. Reutilise BrowserPane pour l'apercu.
function DropBlocks({ exercise, onChange, disabled, revealed, verdict, onTile, onDrop, lang }) {
    const t = useTranslations();
    const data = exercise.data || {};
    const template = data.template || "";
    const blocks = data.blocks || [];
    const preview = !!data.preview;
    const s = exercise.solution || {};
    // Plusieurs agencements valides possibles (ex. <strong><em> ou <em><strong>).
    const accepts = s.accept || (s.slots ? [s.slots] : []);
    const active = !disabled && !revealed;

    const slotIdxs = [];
    template.replace(/\{(\d+)\}/g, (mm, d) => {
        slotIdxs.push(parseInt(d, 10));
        return mm;
    });
    const slotCount = slotIdxs.length ? Math.max(...slotIdxs) + 1 : 0;

    // Coloration du gabarit ENTIER (les marqueurs {0} comptent comme du texte
    // ordinaire), puis chaque segment reçoit les jetons de sa tranche : un
    // segment colorié seul perdrait le contexte de sa règle ou de sa balise.
    const dropLanguage = codeLang(template, data.language, lang);
    const templateTokens = tokenizeLines(template, dropLanguage);
    const lines = template.split("\n").map((ln, li) => {
        const segs = [];
        const re = /\{(\d+)\}/g;
        let last = 0;
        let m;
        const push = (from, to) => {
            segs.push({
                t: ln.slice(from, to),
                k: sliceTokens(templateTokens[li] || [], from, to),
            });
        };
        while ((m = re.exec(ln)) !== null) {
            if (m.index > last) push(last, m.index);
            segs.push({ s: parseInt(m[1], 10) });
            last = m.index + m[0].length;
        }
        if (last < ln.length) push(last, ln.length);
        return segs;
    });

    const [fill, setFill] = useState({});
    const [drag, setDrag] = useState(null);
    const [ghost, setGhost] = useState({ x: 0, y: 0 });
    const [over, setOver] = useState(null);
    const movedRef = useRef(false);
    const startRef = useRef({ x: 0, y: 0 });

    function emit(f) {
        const ans = [];
        for (let i = 0; i < slotCount; i++) ans.push(f[i] != null ? f[i] : null);
        onChange(ans, ans.length > 0 && ans.every((v) => v != null));
    }

    useEffect(() => {
        emit(fill);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function place(slot, blockIdx) {
        const next = {};
        Object.keys(fill).forEach((k) => {
            if (fill[k] !== blockIdx) next[k] = fill[k];
        });
        next[slot] = blockIdx;
        setFill(next);
        emit(next); // hors updater : evite le setState parent pendant le rendu
        if (onDrop) onDrop();
        else if (onTile) onTile();
    }
    function clearSlot(slot) {
        const next = { ...fill };
        delete next[slot];
        setFill(next);
        emit(next);
        if (onTile) onTile();
    }

    function beginDrag(e, blockIdx) {
        if (!active) return;
        movedRef.current = false;
        startRef.current = { x: e.clientX, y: e.clientY };
        setGhost({ x: e.clientX, y: e.clientY });
        setDrag(blockIdx);
    }

    useEffect(() => {
        if (drag == null) return undefined;
        function slotAt(e) {
            const el = document.elementFromPoint(e.clientX, e.clientY);
            const sl = el && el.closest ? el.closest("[data-dropslot]") : null;
            return sl ? parseInt(sl.dataset.dropslot, 10) : null;
        }
        function onMove(e) {
            if (
                !movedRef.current &&
                Math.hypot(
                    e.clientX - startRef.current.x,
                    e.clientY - startRef.current.y
                ) > 5
            )
                movedRef.current = true;
            setGhost({ x: e.clientX, y: e.clientY });
            const s = slotAt(e);
            setOver(s != null && fill[s] == null ? s : null);
        }
        function onUp(e) {
            const s = slotAt(e);
            if (s != null && fill[s] == null) place(s, drag);
            setDrag(null);
            setOver(null);
        }
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drag, fill]);

    const used = new Set(Object.values(fill));
    const previewHtml = template.replace(/\{(\d+)\}/g, (mm, d) => {
        const bi = fill[parseInt(d, 10)];
        return bi != null ? blocks[bi] : "";
    });

    // Revelation : agencement accepte le plus proche de la reponse (vert/rouge par slot).
    const revealRef = (() => {
        if (!revealed || !accepts.length) return null;
        const ans = [];
        for (let i = 0; i < slotCount; i++) ans.push(fill[i] != null ? fill[i] : null);
        let best = accepts[0];
        let score = -1;
        accepts.forEach((a) => {
            let sc = 0;
            for (let i = 0; i < slotCount; i++) if (a[i] === ans[i]) sc += 1;
            if (sc > score) {
                score = sc;
                best = a;
            }
        });
        return best;
    })();

    function slotEl(slotIdx) {
        const bi = fill[slotIdx];
        if (bi != null) {
            const cls = revealed
                ? revealRef && revealRef[slotIdx] === bi
                    ? "border-emerald-500 bg-emerald-500/15"
                    : "border-red-500 bg-red-500/15"
                : "border-[#3a4a5c] bg-[#2a2f37] hover:border-[#4b6a8a]";
            return (
                <button
                    key={`s${slotIdx}`}
                    type="button"
                    data-dropslot={slotIdx}
                    disabled={!active}
                    onClick={() => active && clearSlot(slotIdx)}
                    style={{ color: "#9cdcfe" }}
                    className={`mx-0.5 inline-flex h-7 items-center rounded-md border px-2 align-middle transition-colors ${cls} ${
                        active ? "cursor-pointer" : "cursor-default"
                    }`}
                >
                    {blocks[bi]}
                </button>
            );
        }
        // Emplacement vide : TAILLE FIXE (pas de decalage au survol, seul le style change).
        return (
            <span
                key={`s${slotIdx}`}
                data-dropslot={slotIdx}
                className={`mx-0.5 inline-flex h-7 w-14 items-center justify-center rounded-md border border-dashed align-middle text-[#5a728c] transition-colors ${
                    over === slotIdx
                        ? "border-[#7fc4ff] bg-[#569cd6]/25"
                        : "border-[#4b6a8a] bg-[#569cd6]/[0.08]"
                }`}
            >
                ···
            </span>
        );
    }

    const editor = (
        <div className="flex flex-col overflow-hidden rounded-xl border border-[#2a2a2a] bg-[#1e1e1e]">
            <div className="flex shrink-0 items-center gap-2 border-b border-[#333] bg-[#252526] px-3 py-2">
                <span className="h-2.5 w-2.5 rounded-sm bg-orange-500" />
                <span className="font-geistMono text-xs text-gray-300">
                    {/* La langue EFFECTIVE du gabarit, pas celle du parcours :
                        un gabarit de CSS pur s'annonce « <style> », comme dans
                        tous les autres panneaux. */}
                    {panelFileName(exercise.data, dropLanguage)}
                </span>
            </div>
            <div className="py-2.5 pr-3 font-geistMono text-[13px] leading-[2.5] text-[#d4d4d4]">
                {lines.map((segs, li) => (
                    <div key={li} className="flex">
                        <div className="w-10 shrink-0 select-none pr-3 text-right text-[#6e7681]">
                            {li + 1}
                        </div>
                        {/* min-w-0 + flex-1 : le code REVIENT A LA LIGNE au lieu d'etre coupe */}
                        <div className="min-w-0 flex-1 border-l border-[#2a2a2a] pl-3">
                            {segs.map((seg, si) =>
                                seg.t != null ? (
                                    <span key={si} className="whitespace-pre-wrap">
                                        {renderTokensDark(seg.k)}
                                    </span>
                                ) : (
                                    slotEl(seg.s)
                                )
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div>
            {!revealed && (
                <p className="mb-3 text-xs text-gray-500 dark:text-grayDark-400">
                    {t("reps_drop_blocks_instruction")}
                </p>
            )}
            <PreviewSplit
                preview={preview}
                editor={editor}
                previewNode={<BrowserPane html={previewHtml} />}
            />

            <div className="mt-3 rounded-xl border border-dashed border-gray-300 p-3 dark:border-white/15">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    {t("reps_blocks_to_place")}
                </p>
                <div className="flex flex-wrap gap-2">
                    {blocks.map((b, i) => (
                        <button
                            key={i}
                            type="button"
                            disabled={!active || used.has(i)}
                            onPointerDown={(e) => {
                                e.preventDefault();
                                beginDrag(e, i);
                            }}
                            style={{ touchAction: "none" }}
                            className={`rounded-md border border-[#3a3a3a] bg-[#1e1e1e] px-3 py-1.5 font-geistMono text-[13px] text-[#9cdcfe] shadow-sm transition-opacity ${
                                used.has(i)
                                    ? "opacity-25"
                                    : active
                                      ? "cursor-grab active:cursor-grabbing"
                                      : "cursor-default"
                            } ${drag === i ? "opacity-40" : ""}`}
                        >
                            {b}
                        </button>
                    ))}
                </div>
            </div>

            {/* Révélation : le gabarit rempli, exécuté, au bon verdict. */}
            {verdict === true && exercise.data.reveal && executableLang(lang) && (
                <RevealConsole
                    page={exercise.data.page}
                    code={previewHtml}
                    inputs={exercise.data.inputs}
                    filename={panelFileName(exercise.data, lang)}
                    language={lang}
                />
            )}

            {drag != null &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        className="pointer-events-none fixed z-[200] -translate-x-1/2 -translate-y-1/2 scale-105 rounded-md border border-[#569cd6] bg-[#1e1e1e] px-3 py-1.5 font-geistMono text-[13px] text-[#9cdcfe] shadow-xl"
                        style={{ left: ghost.x, top: ghost.y }}
                    >
                        {blocks[drag]}
                    </div>,
                    document.body
                )}
        </div>
    );
}

// Couleurs de paires (façon Mimo) : chaque liaison a sa couleur, portée par les
// deux cartes ET par le trait qui les relie. Classes littérales (Tailwind-safe).
const MATCH_COLORS = [
    { stroke: "#3b82f6", dot: "bg-blue-500", card: "border-blue-300 border-b-blue-400 bg-blue-50 text-blue-900 dark:border-blue-500/40 dark:border-b-blue-500/50 dark:bg-blue-500/10 dark:text-blue-100" },
    { stroke: "#f97316", dot: "bg-orange-500", card: "border-orange-300 border-b-orange-400 bg-orange-50 text-orange-900 dark:border-orange-500/40 dark:border-b-orange-500/50 dark:bg-orange-500/10 dark:text-orange-100" },
    { stroke: "#10b981", dot: "bg-emerald-500", card: "border-emerald-300 border-b-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-500/40 dark:border-b-emerald-500/50 dark:bg-emerald-500/10 dark:text-emerald-100" },
    { stroke: "#8b5cf6", dot: "bg-violet-500", card: "border-violet-300 border-b-violet-400 bg-violet-50 text-violet-900 dark:border-violet-500/40 dark:border-b-violet-500/50 dark:bg-violet-500/10 dark:text-violet-100" },
    { stroke: "#ec4899", dot: "bg-pink-500", card: "border-pink-300 border-b-pink-400 bg-pink-50 text-pink-900 dark:border-pink-500/40 dark:border-b-pink-500/50 dark:bg-pink-500/10 dark:text-pink-100" },
    { stroke: "#f59e0b", dot: "bg-amber-500", card: "border-amber-300 border-b-amber-400 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:border-b-amber-500/50 dark:bg-amber-500/10 dark:text-amber-100" },
];

// Permutation aléatoire des index 0..n-1 (Fisher-Yates).
function shuffleIndices(n) {
    const a = Array.from({ length: n }, (_, i) => i);
    for (let k = n - 1; k > 0; k--) {
        const r = Math.floor(Math.random() * (k + 1));
        const tmp = a[k];
        a[k] = a[r];
        a[r] = tmp;
    }
    return a;
}

// Ordre d'AFFICHAGE des deux colonnes d'un match_pairs. On mélange gauche ET
// droite (pour que rien ne soit jamais dans le même ordre) tout en évitant
// qu'une réponse tombe en face de son item (sinon la correspondance est
// offerte). Les index RÉELS sont conservés partout (refs, map, correction,
// couleurs) : seul l'ordre visuel change. `sol` = solution.pairs ([i, j]).
// On vise 0 alignement ; si le contenu rend ça impossible (réponse partagée,
// moins de droites que de gauches, une seule ligne...), on renvoie le mélange
// le MOINS aligné trouvé (best-effort) plutôt qu'un mélange au hasard.
function shuffledMatchOrder(leftLen, rightLen, sol) {
    const answerFor = (i) => {
        const p = sol.find((x) => x[0] === i);
        return p ? p[1] : null;
    };
    const alignedCount = (leftOrder, rightOrder) => {
        let n = 0;
        for (let pos = 0; pos < leftLen && pos < rightLen; pos++) {
            if (rightOrder[pos] === answerFor(leftOrder[pos])) n++;
        }
        return n;
    };
    let best = null;
    let bestScore = Infinity;
    for (let attempt = 0; attempt < 60; attempt++) {
        const leftOrder = shuffleIndices(leftLen);
        const rightOrder = shuffleIndices(rightLen);
        const score = alignedCount(leftOrder, rightOrder);
        if (score === 0) return { leftOrder, rightOrder };
        if (score < bestScore) {
            bestScore = score;
            best = { leftOrder, rightOrder };
        }
    }
    return (
        best || {
            leftOrder: shuffleIndices(leftLen),
            rightOrder: shuffleIndices(rightLen),
        }
    );
}

// Etiquette d'une carte match_pairs : icone OPTIONNELLE (Solar coloree ou logo de
// marque via Iconify) + texte. Sans icone, on rend juste le texte.
function MatchLabel({ ic, txt }) {
    if (!ic) return renderRich(txt);
    return (
        <span className="flex items-center gap-2.5">
            <Icon
                icon={ic.icon}
                className="shrink-0 text-[20px]"
                style={ic.color ? { color: ic.color } : undefined}
            />
            <span>{renderRich(txt)}</span>
        </span>
    );
}

// Miniature de rendu (match VISUEL : « relie chaque code à la page qu'il
// produit ») : le vrai moteur d'aperçu (mêmes styles UA que BrowserPane), sans
// chrome, zoomé à 50 % pour montrer plus de page, et INERTE au pointeur — le
// clic traverse et touche la carte. Le texte de la carte devient l'aria-label.
function MiniPane({ html, label, height = 84 }) {
    return (
        <span
            className="pointer-events-none block overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-white/10"
            style={{ height }}
            role="img"
            aria-label={label || ""}
        >
            <iframe
                title={label || "preview"}
                aria-hidden
                tabIndex={-1}
                sandbox="allow-same-origin"
                srcDoc={srcDoc(html)}
                className="block border-0"
                style={{
                    width: "200%",
                    height: height * 2,
                    transform: "scale(0.5)",
                    transformOrigin: "0 0",
                    pointerEvents: "none",
                }}
            />
        </span>
    );
}

function MatchPairs({ exercise, onChange, disabled, revealed, onTile }) {
    const t = useTranslations();
    const left = exercise.data.left || [];
    const right = exercise.data.right || [];
    // Icones OPTIONNELLES (une par item, alignees sur left/right) : chaine
    // "solar:xxx" (ou "logos:xxx" pour une marque) ou objet { icon, color }.
    const leftIcons = exercise.data.leftIcons || [];
    const rightIcons = exercise.data.rightIcons || [];
    // Match VISUEL (optionnel) : rightHtml[j] non vide -> la carte de droite rend
    // une MINIATURE de page (MiniPane) au lieu du texte ; right[j] reste la
    // description (aria-label + correction lisible). "" = carte texte classique.
    const rightHtml = exercise.data.rightHtml || [];
    const iconOf = (arr, i) =>
        arr[i] ? (typeof arr[i] === "string" ? { icon: arr[i] } : arr[i]) : null;
    const sol = exercise.solution.pairs || [];
    const [map, setMap] = useState({}); // { leftIndex: rightIndex } — contrat de correction inchangé
    const [selLeft, setSelLeft] = useState(-1);
    const [lines, setLines] = useState([]);
    // Ordre d'affichage des deux colonnes. 1er rendu = ordre naturel (identique
    // au HTML serveur -> pas de mismatch d'hydratation), puis on mélange au
    // montage AVANT peinture (useLayoutEffect -> pas de flash de l'ordre aligné).
    // Le player remonte l'exercice à chaque visite (key=visit) : nouveau mélange
    // à chaque passage sur l'étape. Les index restent réels, seul l'ordre change.
    const [order, setOrder] = useState(() => ({
        leftOrder: left.map((_, i) => i),
        rightOrder: right.map((_, j) => j),
    }));

    const wrapRef = useRef(null);
    const leftRefs = useRef([]);
    const rightRefs = useRef([]);

    useLayoutEffect(() => {
        setOrder(shuffledMatchOrder(left.length, right.length, sol));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function report(next) {
        onChange(next, Object.keys(next).length === left.length);
    }
    function correctRightFor(i) {
        const p = sol.find((x) => x[0] === i);
        return p ? p[1] : null;
    }
    // Couleur stable d'une paire = index de l'élément de gauche.
    const colorFor = (i) => MATCH_COLORS[i % MATCH_COLORS.length];
    const leftOfRight = (j) =>
        Object.keys(map).find((i) => map[i] === j);

    function pickLeft(i) {
        if (disabled) return;
        if (onTile) onTile();
        if (map[i] != null) {
            // Déjà relié : on défait la liaison (les deux cartes se libèrent).
            const next = { ...map };
            delete next[i];
            setMap(next);
            setSelLeft(-1);
            report(next);
            return;
        }
        setSelLeft(selLeft === i ? -1 : i);
    }
    function pickRight(j) {
        if (disabled) return;
        if (onTile) onTile();
        const owner = leftOfRight(j);
        if (owner != null) {
            // Cette carte de droite est déjà reliée : on défait.
            const next = { ...map };
            delete next[owner];
            setMap(next);
            setSelLeft(-1);
            report(next);
            return;
        }
        if (selLeft < 0) return;
        const next = { ...map, [selLeft]: j };
        setMap(next);
        setSelLeft(-1);
        report(next);
    }

    // Recalcule les traits (positions réelles des cartes) à chaque changement de
    // liaisons, au redimensionnement, et à la révélation.
    useLayoutEffect(() => {
        function compute() {
            const wrap = wrapRef.current;
            if (!wrap) return;
            const wb = wrap.getBoundingClientRect();
            const next = [];
            Object.keys(map).forEach((iStr) => {
                const i = Number(iStr);
                const j = map[i];
                const l = leftRefs.current[i];
                const r = rightRefs.current[j];
                if (!l || !r) return;
                const lb = l.getBoundingClientRect();
                const rb = r.getBoundingClientRect();
                let stroke = colorFor(i).stroke;
                if (revealed)
                    stroke = j === correctRightFor(i) ? "#22c55e" : "#ef4444";
                next.push({
                    key: i,
                    x1: lb.right - wb.left,
                    y1: lb.top + lb.height / 2 - wb.top,
                    x2: rb.left - wb.left,
                    y2: rb.top + rb.height / 2 - wb.top,
                    stroke,
                });
            });
            setLines(next);
        }
        compute();
        const ro =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(compute)
                : null;
        if (ro && wrapRef.current) ro.observe(wrapRef.current);
        window.addEventListener("resize", compute);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener("resize", compute);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, revealed]);

    function cardClass(paired, colorCard, correct) {
        if (revealed && paired) return correct ? C_GOOD : C_BAD;
        if (paired) return colorCard;
        return C_NEUTRAL;
    }

    return (
        <div>
            <p className="mb-2.5 text-xs text-gray-500">
                {t("reps_match_pairs_instruction")}
            </p>
            <div ref={wrapRef} className="relative">
                {/* Traits de liaison (au-dessus, sans intercepter les clics) */}
                <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible">
                    {lines.map((ln) => (
                        <path
                            key={ln.key}
                            d={`M ${ln.x1} ${ln.y1} C ${ln.x1 + 44} ${ln.y1}, ${ln.x2 - 44} ${ln.y2}, ${ln.x2} ${ln.y2}`}
                            fill="none"
                            stroke={ln.stroke}
                            strokeWidth="3"
                            strokeLinecap="round"
                        />
                    ))}
                </svg>

                <div className="flex gap-10 sm:gap-14">
                    <div className="flex-1 space-y-2.5">
                        {order.leftOrder.map((i) => {
                            const txt = left[i];
                            const paired = map[i] != null;
                            const col = colorFor(i);
                            const sel = selLeft === i;
                            return (
                                <button
                                    key={i}
                                    ref={(el) => (leftRefs.current[i] = el)}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => pickLeft(i)}
                                    className={`${CARD} relative ${cardClass(paired, col.card, map[i] === correctRightFor(i))} ${
                                        sel ? "ring-2 ring-primary ring-offset-1" : ""
                                    }`}
                                >
                                    <MatchLabel ic={iconOf(leftIcons, i)} txt={txt} />
                                    {/* Pastille de liaison sur le bord intérieur (droite) */}
                                    {paired && !revealed && (
                                        <span
                                            className={`absolute -right-[7px] top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border-2 border-white ${col.dot} dark:border-[#10171E]`}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex-1 space-y-2.5">
                        {order.rightOrder.map((j) => {
                            const txt = right[j];
                            const owner = leftOfRight(j);
                            const paired = owner != null;
                            const col = paired ? colorFor(Number(owner)) : null;
                            const correct =
                                paired && map[Number(owner)] === correctRightFor(Number(owner));
                            return (
                                <button
                                    key={j}
                                    ref={(el) => (rightRefs.current[j] = el)}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => pickRight(j)}
                                    className={`${CARD} relative ${cardClass(paired, col ? col.card : "", correct)}`}
                                >
                                    {rightHtml[j] ? (
                                        <MiniPane
                                            html={rightHtml[j]}
                                            label={txt}
                                        />
                                    ) : (
                                        <MatchLabel
                                            ic={iconOf(rightIcons, j)}
                                            txt={txt}
                                        />
                                    )}
                                    {paired && !revealed && (
                                        <span
                                            className={`absolute -left-[7px] top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border-2 border-white ${col.dot} dark:border-[#10171E]`}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function DragDrop({ exercise, onChange, disabled, revealed }) {
    const t = useTranslations();
    const items = exercise.data.items || [];
    // Ordre d'AFFICHAGE mélangé du vivier : dans l'ordre du contenu, les
    // étiquettes se rangent par position sans réfléchir. Les index restent les
    // index RÉELS (la correction ne bouge pas), seul l'affichage change. 1er
    // rendu = ordre naturel (pas de mismatch d'hydratation), mélange au montage.
    const [itemOrder, setItemOrder] = useState(() => items.map((_, k) => k));
    useLayoutEffect(() => {
        setItemOrder(shuffleIndices(items.length));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const zones = exercise.data.zones || [];
    const empile = zones.length > 2;
    const sol = exercise.solution.placement || [];
    const [placement, setPlacement] = useState(() => Array(items.length).fill(null));
    const [sel, setSel] = useState(-1);

    function report(next) {
        onChange(
            next,
            next.every((z) => z != null)
        );
    }
    function place(itemIdx, zoneIdx) {
        if (disabled || itemIdx == null) return;
        const next = [...placement];
        next[itemIdx] = zoneIdx;
        setPlacement(next);
        setSel(-1);
        report(next);
    }
    function chipClass(i) {
        if (revealed) {
            return placement[i] === sol[i]
                ? "border-green-500 bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300"
                : "border-red-500 bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300";
        }
        if (sel === i)
            return "border-[#8b7ff0] bg-[#8b7ff0]/10 text-[#5b4fd0] dark:bg-[#8b7ff0]/15 dark:text-white";
        return "border-gray-200 bg-white text-gray-800 shadow-sm dark:border-white/10 dark:bg-white/[0.06] dark:text-grayDark-100 dark:shadow-none";
    }
    function Chip({ i, ghost }) {
        // Étiquette déjà rangée : elle reste VISIBLE mais grisée à sa place dans
        // le vivier (comme drop_blocks), au lieu de disparaître. Rien ne se
        // décale, on voit d'un coup d'œil ce qui reste à ranger.
        // muted : le code inline de l'étiquette s'estompe AVEC elle, sinon son
        // orange restait vif et l'étiquette rangée avait l'air à moitié active.
        if (ghost) {
            return (
                <span
                    aria-hidden="true"
                    className="select-none rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-[13px] text-gray-300 dark:border-white/10 dark:bg-white/[0.02] dark:text-grayDark-600"
                >
                    {renderRich(items[i], { muted: true })}
                </span>
            );
        }
        return (
            <button
                type="button"
                draggable={!disabled}
                disabled={disabled}
                onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
                onClick={() => !disabled && setSel(sel === i ? -1 : i)}
                className={`cursor-grab rounded-lg border px-3 py-2 text-[13px] ${chipClass(i)}`}
            >
                {renderRich(items[i])}
            </button>
        );
    }

    return (
        <div>
            <p className="mb-2.5 text-xs text-gray-500">
                {t("reps_drag_drop_instruction")}
            </p>
            <div
                className="mb-3 flex min-h-[48px] flex-wrap gap-2 rounded-xl border border-dashed border-gray-300 p-2 dark:border-white/15"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    const i = parseInt(e.dataTransfer.getData("text/plain"), 10);
                    if (!Number.isNaN(i)) place(i, null);
                }}
            >
                {/* Toutes les étiquettes, à position FIXE : celles déjà rangées
                    passent en grisé (ghost) au lieu de quitter le vivier. */}
                {itemOrder.map((i) => (
                    <Chip key={i} i={i} ghost={placement[i] != null} />
                ))}
            </div>
            {/* À trois zones, la rangée écrase chaque libellé sur quatre lignes
                dès qu'on est sur un téléphone : on empile jusqu'à sm, où les
                colonnes redeviennent lisibles. Empilées, les étiquettes rangées
                se posent en ligne plutôt qu'en colonne, sinon la zone s'allonge
                pour rien. */}
            <div
                className={
                    empile
                        ? "flex flex-col gap-2 sm:flex-row sm:gap-3"
                        : "flex gap-3"
                }
            >
                {zones.map((z, zi) => (
                    <div
                        key={zi}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            e.preventDefault();
                            const i = parseInt(
                                e.dataTransfer.getData("text/plain"),
                                10
                            );
                            if (!Number.isNaN(i)) place(i, zi);
                        }}
                        onClick={() => sel >= 0 && place(sel, zi)}
                        className="flex-1 rounded-xl border border-gray-200 bg-gray-50 p-2.5 dark:border-white/10 dark:bg-white/[0.03]"
                    >
                        <div
                            className={`mb-2 text-xs font-medium text-gray-600 dark:text-grayDark-300 ${
                                empile ? "text-left sm:text-center" : "text-center"
                            }`}
                        >
                            {z.label}
                        </div>
                        <div
                            className={
                                empile
                                    ? "flex min-h-[38px] flex-wrap gap-1.5 sm:min-h-[70px] sm:flex-col sm:flex-nowrap sm:items-stretch"
                                    : "flex min-h-[70px] flex-col items-stretch gap-1.5"
                            }
                        >
                            {items
                                .map((_, i) => i)
                                .filter((i) => placement[i] === zi)
                                .map((i) => (
                                    <Chip key={i} i={i} />
                                ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Rendu riche d'une consigne : balises <...>, backticks `...` et noms h1..h6 -> style code.
// La logique pure (RICH_RE, liens, emphase, blocs) vit dans lib/reps/mdlite.js :
// elle est PARTAGÉE avec le serveur de voix, qui énumère les mots affichés dans
// le même ordre que ce rendu pour aligner le karaoké de lecture.

export function renderRich(text, opts) {
    // split() avec UN seul groupe capturant place les fragments de code aux index
    // IMPAIRS et le texte normal aux index pairs : on style les impairs en code
    // (en retirant les backticks éventuels). Robuste, pas de .test() sur /g.
    // Exporté : aussi utilisé par le player (prompts) et le chat BeBot (RepsAssistant).
    // opts.muted (FACULTATIF, tous les appels existants restent inchangés) : le
    // code inline s'estompe avec le texte qui l'entoure, pour les étiquettes grisées.
    const codeClass = opts && opts.muted ? INLINE_CODE_MUTED : INLINE_CODE;
    const parts = (text || "").split(RICH_RE);
    return parts.map((p, i) => {
        if (!p) return null;
        if (i % 2 === 1) {
            const inner = /^`[^`]+`$/.test(p) ? p.slice(1, -1) : p;
            return (
                <code key={i} className={codeClass}>
                    {inner}
                </code>
            );
        }
        return <span key={i}>{p}</span>;
    });
}

// --- Markdown LÉGER (construit PAR-DESSUS renderRich) -----------------------
// Sous-ensemble volontairement minimal, sûr pour les 600+ leçons existantes
// écrites en texte brut : [liens](https://...), **gras** et *italique* en
// inline, paragraphes et listes "- " en blocs (body de concept uniquement).
// Le code inline garde EXACTEMENT le rendu de renderRich : on réutilise son
// split (segments code aux index impairs), le gras/italique ne touche que les
// segments TEXTE, jamais l'intérieur d'un backtick. Les liens sont parsés
// AVANT l'emphase, et jamais à l'intérieur d'un segment backtick.

// La logique pure (flanquement CommonMark, liens http(s) uniquement, blocs) est
// importée de lib/reps/mdlite.js — voir ses commentaires pour les règles.

// KARAOKÉ DE LECTURE : quand BeBot lit un concept, chaque mot affiché est
// enveloppé dans un <span data-kw={n}> numéroté dans l'ORDRE DU RENDU — le même
// ordre que l'énumération serveur (lib/bebotSpeechText.js, via mdlite). Les
// spans sont INERTES : aucun style tant que le pilote du player (RepsLessonPlayer)
// ne pose pas ses classes. `wrap` est un compteur { n } muté pendant le rendu,
// déterministe (même body -> mêmes index).
function wrapTokens(text, wrap) {
    return String(text)
        .split(/(\s+)/)
        .map((p, k) => {
            if (!p) return null;
            if (/^\s+$/.test(p)) return <Fragment key={k}>{p}</Fragment>;
            return (
                <span key={k} data-kw={wrap.n++}>
                    {p}
                </span>
            );
        });
}

// Rendu inline SANS liens : code (renderRich, inchangé) + **gras** + *italique*.
// Un texte SANS marqueur rend STRICTEMENT la même chose que renderRich.
function renderInlineNoLinks(text, wrap) {
    const parts = (text || "").split(RICH_RE);
    return parts.map((p, i) => {
        if (!p) return null;
        if (i % 2 === 1) {
            const inner = /^`[^`]+`$/.test(p) ? p.slice(1, -1) : p;
            return (
                <code key={i} className={INLINE_CODE}>
                    {wrap ? wrapTokens(inner, wrap) : inner}
                </code>
            );
        }
        const runs = splitEmphasis(p);
        if (runs.length === 1 && runs[0].type === "text") {
            return <span key={i}>{wrap ? wrapTokens(p, wrap) : p}</span>;
        }
        return (
            <span key={i}>
                {runs.map((r, j) =>
                    r.type === "strong" ? (
                        <strong key={j} className="font-semibold">
                            {wrap ? wrapTokens(r.text, wrap) : r.text}
                        </strong>
                    ) : r.type === "em" ? (
                        <em key={j}>{wrap ? wrapTokens(r.text, wrap) : r.text}</em>
                    ) : (
                        <span key={j}>
                            {wrap ? wrapTokens(r.text, wrap) : r.text}
                        </span>
                    )
                )}
            </span>
        );
    });
}

// Style des liens du markdown léger : toujours ouverts dans un NOUVEL onglet
// (l'apprenant ne quitte jamais sa leçon), bleu façon lien de confiance.
const MD_LINK_CLASS =
    "font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 transition-colors hover:text-blue-700 dark:text-blue-400 dark:decoration-blue-500/40 dark:hover:text-blue-300";

// Rendu inline complet : liens [libellé](https://...) d'abord (jamais dans un
// backtick), puis code/gras/italique — y compris DANS le libellé d'un lien.
// Un texte sans lien passe tel quel dans le pipeline d'avant (non-régression).
export function renderMarkdownInline(text, wrap) {
    const segs = splitLinks(text);
    if (segs.length === 1 && segs[0].type === "text") {
        return renderInlineNoLinks(segs[0].text, wrap);
    }
    return segs.map((s, i) =>
        s.type === "link" ? (
            <a
                key={i}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className={MD_LINK_CLASS}
            >
                {renderInlineNoLinks(s.label, wrap)}
            </a>
        ) : (
            <Fragment key={i}>{renderInlineNoLinks(s.text, wrap)}</Fragment>
        )
    );
}

// Rendu en blocs (body de concept) : paragraphes, listes "- ", <br> pour le
// \n simple, inline (code/gras/italique) à l'intérieur. pClassName est posé
// sur chaque bloc pour conserver la typo du champ (non-régression : un body
// d'une seule ligne rend le même <p> qu'avant).
export function renderMarkdownBlocks(text, pClassName = "", opts) {
    // opts.karaoke : numérote chaque mot (voir wrapTokens). Un seul compteur
    // pour tout le body — c'est l'index global qu'attend l'alignement serveur.
    const wrap = opts && opts.karaoke ? { n: 0 } : undefined;
    return splitBlocks(text).map((b, i) => {
        if (b.type === "ul") {
            return (
                <ul
                    key={i}
                    className={`mb-3 list-disc space-y-1 pl-5 marker:text-gray-400 dark:marker:text-grayDark-500 ${pClassName}`.trim()}
                >
                    {b.items.map((it, j) => (
                        <li key={j} className="leading-relaxed">
                            {renderMarkdownInline(it, wrap)}
                        </li>
                    ))}
                </ul>
            );
        }
        return (
            <p key={i} className={pClassName || undefined}>
                {b.lines.map((ln, j) => (
                    <Fragment key={j}>
                        {j > 0 && <br />}
                        {renderMarkdownInline(ln, wrap)}
                    </Fragment>
                ))}
            </p>
        );
    });
}

// Coloration syntaxique d'un FRAGMENT de code (fill_blank). Un trou coupe la
// balise en deux (« <img » d'un côté, « ="chat.jpg"> » de l'autre) : on ne peut
// donc pas se contenter de reconnaitre des balises entières, il faut colorer


// Corps d'une console Python, PARTAGÉ par l'exercice `python_code` et la
// démonstration d'un concept : deux consoles qui divergent, ce sont deux vérités.
//
// Le rendu imite un vrai terminal SANS rien enseigner de faux. Mesuré en
// exécutant python3 : `python main.py` affiche sa sortie BRUTE, octet pour
// octet, sans le moindre préfixe. Le chevron `>>>` appartient au mode
// interactif, et il se place devant ce qu'on TAPE, jamais devant un résultat —
// en mettre un devant chaque ligne de sortie apprendrait à l'apprenant une
// règle qu'il trouverait fausse à son premier vrai terminal.
//
// Ce qui donne la sensation de terminal, c'est donc l'ENTRÉE : la commande
// lancée en tête (le geste réel qu'il fera un jour), la sortie brute dessous,
// et le prompt qui revient à la fin pour dire que c'est terminé.
function PythonConsoleBody({ out, running, filename, idle, language = "python", silence }) {
    const t = useTranslations();
    if (out?.unavailable) {
        return (
            <span className="text-gray-500">
                {t(language === "javascript" ? "reps_js_unavailable" : "reps_python_unavailable")}
            </span>
        );
    }
    if (!out && !running) return <span className="text-gray-500">{idle}</span>;
    // `print` termine toujours sa ligne : le \n final amènerait le prompt une
    // ligne trop bas, alors qu'un terminal le pose juste dessous.
    const stdout = (out?.stdout || "").replace(/\n+$/, "");
    const stderr = (out?.stderr || "").replace(/\n+$/, "");
    const error = (out?.error || "").replace(/\n+$/, "");
    const muet = out && !stdout && !stderr && !error;
    return (
        <>
            <div className="text-gray-500">
                <span className="select-none">$ </span>
                {commandFor(language)} {filename}
            </div>
            {/* stdout reste CLAIR même quand le programme a fini en erreur : ce
                sont deux flux distincts, et ce qui a été affiché avant
                l'exception l'a bien été. */}
            {stdout && <div className="text-gray-100">{stdout}</div>}
            {error && <div className="text-red-400">{error}</div>}
            {stderr && <div className="text-red-400">{stderr}</div>}
            {muet && (
                <div className="text-emerald-400">
                    {silence || t("reps_python_no_output")}
                </div>
            )}
            {out && (
                <div className="text-gray-500">
                    <span className="select-none">$ </span>
                    <span className="inline-block h-[1em] w-[0.6em] translate-y-[0.15em] animate-pulse bg-gray-500" />
                </div>
            )}
        </>
    );
}

// LA RÉVÉLATION (`data.reveal`) : quand l'apprenant vient d'assembler une ligne
// et qu'il a BON, on exécute ce qu'il a écrit et on lui montre ce que ça
// produit. Sans elle il a rangé des étiquettes, il n'a pas vu de programme.
//
// C'est SA réponse qu'on exécute, pas la solution : sur ces trois types la
// correction est une égalité stricte, donc au bon verdict les deux sont
// identiques, et « c'est moi qui l'ai fait » est tout l'intérêt.
//
// FILET, et c'est le cœur : on n'affiche RIEN tant qu'il n'y a pas une vraie
// sortie. Mesuré sur le corpus, 45 % des lignes reconstruites lèveraient une
// exception (une variable qui ne vit que dans la consigne, un `input()`, un
// fragment non exécutable) et 14 % ne produiraient rien. Une trace rouge ou une
// console vide juste après un bon verdict, c'est une récompense qui punit.
function RevealConsole(props) {
    // `data.page` : le code assemblé agit sur une page, pas dans la console.
    if (props.page) return <RevealPage code={props.code} page={props.page} />;
    return <RevealConsoleRun {...props} />;
}

function RevealConsoleRun({ code, inputs, filename, language = "python" }) {
    const [out, setOut] = useState(null);
    // Les réponses préremplies sont un tableau littéral du fichier leçon : le
    // comparer par identité relancerait l'exécution à chaque rendu.
    const reponses = JSON.stringify(inputs || []);

    useEffect(() => {
        let alive = true;
        warmFor(language);
        runCodeFor(language, code, JSON.parse(reponses)).then((res) => {
            if (alive) setOut(res);
        });
        return () => {
            alive = false;
        };
    }, [code, reponses, language]);

    const sortie = (out?.stdout || "").replace(/\n+$/, "");
    if (!out || !out.ok || !sortie) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="mt-3 overflow-hidden rounded-xl border border-gray-200 dark:border-white/10"
        >
            <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 dark:border-white/5 dark:bg-[#192734] dark:text-grayDark-300">
                <SolarIcon name="programming-linear" className="text-sm" />
                Console
            </div>
            <div className="max-h-60 overflow-auto whitespace-pre-wrap bg-[#0E161D] p-3 font-geistMono text-[13px] leading-relaxed text-gray-100">
                <div className="text-gray-500">
                    <span className="select-none">$ </span>
                    {commandFor(language)} {filename}
                </div>
                <div className="text-gray-100">{sortie}</div>
            </div>
        </motion.div>
    );
}

// Console d'un concept qui EXÉCUTE son code (`data.run`). Rien n'est demandé à
// l'apprenant : la démonstration tourne toute seule au montage, comme un aperçu
// HTML se rend tout seul. Pas de bouton « Exécuter » donc, il n'y a rien à
// déclencher.
//
// Une trace rouge peut être VOULUE ici (la leçon qui apprend à lire un
// NameError) : seul `unavailable` (Python n'a pas pu démarrer, hors ligne, CDN
// injoignable) reçoit un traitement à part, avec une relance à la main, parce
// que celui-là n'enseigne rien.
function ConceptConsole({ code, inputs, height, filename, onHeight, language = "python" }) {
    const t = useTranslations();
    const [out, setOut] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const corps = useRef(null);
    const reponses = JSON.stringify(inputs || []);

    useEffect(() => {
        let alive = true;
        setOut(null);
        warmFor(language);
        runCodeFor(language, code, JSON.parse(reponses)).then((res) => {
            // L'apprenant a pu passer à l'écran suivant pendant l'exécution.
            if (alive) setOut(res);
        });
        return () => {
            alive = false;
        };
    }, [code, reponses, attempt, language]);

    // La console REMONTE sa hauteur réelle, comme l'aperçu navigateur : sans
    // ça elle héritait de la hauteur de l'ÉDITEUR, calculée sur le nombre de
    // lignes du code. Une ligne de code, trois lignes de terminal (la commande,
    // la sortie, le prompt) : la fin était coupée.
    //
    // On mesure le CONTENU, jamais le panneau : le scrollHeight d'un conteneur
    // vaut au moins sa hauteur visible, donc le mesurer ferait grandir le
    // panneau à chaque passage, jusqu'au plafond.
    useEffect(() => {
        if (!corps.current || !onHeight) return;
        // Plancher de trois lignes (la commande, une sortie, le prompt) : la
        // mesure d'avant l'exécution ne vaut qu'une ligne, et le panneau
        // sauterait de taille au moment où le résultat arrive.
        onHeight(Math.max(corps.current.scrollHeight, 63) + 24);
    }, [out, onHeight]);

    return (
        <div
            className="flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-white/10"
            style={{ height }}
        >
            <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-white/5 dark:bg-[#192734]">
                <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-grayDark-300">
                    <SolarIcon name="programming-linear" className="text-sm" />
                    Console
                </span>
                {out?.unavailable && (
                    <button
                        type="button"
                        onClick={() => setAttempt((a) => a + 1)}
                        className="flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
                    >
                        <SolarIcon name="refresh-linear" className="text-xs" />
                        {t("reps_python_retry")}
                    </button>
                )}
            </div>
            <div className="flex-1 overflow-auto whitespace-pre-wrap bg-[#0E161D] p-3 font-geistMono text-[13px] leading-relaxed text-gray-100">
                {/* Ce div interne n'a pas de hauteur imposée : c'est LUI qu'on
                    mesure, sa hauteur est celle du texte et rien d'autre. */}
                <div ref={corps}>
                    <PythonConsoleBody
                        out={out}
                        running={!out}
                        filename={filename}
                        language={language}
                        idle={t(language === "javascript" ? "reps_js_loading" : "reps_python_loading")}
                    />
                </div>
            </div>
        </div>
    );
}

// Affichage du code d'un concept : editeur Monaco read-only + apercu Chrome a la
// MEME hauteur. La hauteur suit le PLUS GRAND des deux contenus (l'apercu peut avoir
// plus de lignes que le code, ex. un <br>), pour ne jamais tronquer la previsualisation.
function ConceptCode({ code, data, lang, preview }) {
    const codeH = codeHeightFor(code);
    // MÊME choix de langue et de nom que toutes les autres étapes (codeLang +
    // panelFileName). Ce panneau-ci décidait seul, sur la seule présence de
    // chevrons : du CSS pur n'en a pas, et un concept qui montre une feuille de
    // style s'affichait donc en Python, sous une étiquette « main.py », alors
    // que la leçon déclarait `language: "css"` et `fileName: "style.css"`.
    const language = codeLang(code, data?.language, lang);
    const filename = panelFileName(data, language);
    // L'aperçu navigateur ne rend que du HTML : pas de panneau pour du Python
    // ni pour du CSS seul (sinon le code s'afficherait en texte brut).
    const showPreview = preview && language === "html" && looksLikeHtml(code);
    // `data.run` : la démonstration Python. Le code est EXÉCUTÉ et sa sortie
    // s'affiche, au lieu d'être racontée par l'auteur dans le texte de l'écran.
    // C'est l'équivalent de l'aperçu pour un parcours sans page à regarder :
    // sans lui, un concept Python ne montre rien, il affirme.
    // Parcours JavaScript : même démonstration, exécutée par le runtime JS.
    const executable = executableLang(language);
    const showRun = !!data?.run && executable !== null;
    // `data.viewport` : l'aperçu devient une FENÊTRE de hauteur fixe, dans
    // laquelle le contenu DÉFILE — au lieu de grandir pour tout montrer, ce qui
    // reste le défaut. Sans ça, `position: fixed` et `position: sticky` sont
    // indémontrables : le premier ne se distingue pas d'un absolu, le second ne
    // fait rien du tout tant que rien ne défile (mesuré le 05/08/2026, voir
    // SECTION-6.md). N'est pas `data.height`, que 178 concepts posent déjà sans
    // effet ici : les faire tous basculer d'un coup changerait leur rendu.
    const viewport = Number.isFinite(data?.viewport)
        ? Math.min(Math.max(data.viewport, 160), 520)
        : null;
    // La hauteur suit le PLUS GRAND des deux panneaux : l'apercu (iframe) remonte
    // sa hauteur reelle, on grandit pour l'englober sans jamais tronquer.
    const [paneH, setPaneH] = useState(codeH);
    function onHeight(h) {
        setPaneH(Math.min(Math.max(codeH, 38 + h), 480));
    }

    if (showRun) {
        // Même règle que l'aperçu : les deux panneaux prennent la hauteur du
        // PLUS GRAND des deux contenus. La console en a souvent plus que le
        // code (une ligne de `print` produit trois lignes de terminal), et une
        // sortie coupée est le contraire de ce que la démonstration promet.
        return (
            <div className="grid gap-3 lg:grid-cols-2">
                <RepsCodeEditor
                    value={code}
                    readOnly
                    language={language}
                    filename={filename}
                    style={{ height: paneH }}
                />
                {/* `key` : deux concepts `run` qui se suivent réutiliseraient
                    la même instance, et la console garderait la sortie de
                    l'écran précédent pendant la nouvelle exécution. */}
                <ConceptConsole
                    key={code}
                    code={code}
                    inputs={data?.inputs}
                    height={paneH}
                    filename={filename}
                    onHeight={onHeight}
                    language={executable || "python"}
                />
            </div>
        );
    }
    if (!showPreview) {
        return (
            <RepsCodeEditor
                value={code}
                readOnly
                language={language}
                filename={filename}
                style={{ height: codeH }}
            />
        );
    }
    return (
        // Empilé (code en haut, aperçu en bas) tant que la place manque ; côte à
        // côte seulement en lg, pour ne pas écraser le code sur colonne étroite.
        <div className="grid gap-3 lg:grid-cols-2">
            <RepsCodeEditor
                value={code}
                readOnly
                language={language}
                filename={filename}
                style={{ height: viewport || paneH }}
            />
            {/* Une fenêtre imposée ne se MESURE pas : sans `onHeight`, l'iframe
                garde sa hauteur et défile toute seule, comme un navigateur. */}
            <BrowserPane
                html={code}
                style={{ height: viewport || paneH }}
                onHeight={viewport ? undefined : onHeight}
            />
        </div>
    );
}

// Concept INTERACTIF "devine & revele" (facon Mimo/Brilliant) : l'apprenant devine
// la ou les lettres cachees d'un mot. Mauvaise lettre -> feedback rouge + secousse ;
// bonne lettre -> elle se remplit en vert ; mot complete -> revelation recompense
// (une image, ou un VRAI rendu HTML dans le navigateur). Aucune notation : c'est un
// moment de plaisir et de curiosite, pas un exercice. Declaratif, entierement FR+EN :
//   { kind:"guess", label, word:"DOG", blanks:[0], choices:["Q","R","D"],
//     solvedLabel, retryLabel, reveal:{ image | html, caption } }
function GuessReveal({ spec }) {
    const word = String(spec.word || "");
    const letters = word.split("");
    const blanks = Array.isArray(spec.blanks) ? spec.blanks : [];
    const choices = Array.isArray(spec.choices) ? spec.choices : [];
    const reveal = spec.reveal || {};
    const [filled, setFilled] = useState(() => new Set());
    const [wrong, setWrong] = useState(null); // lettre fausse en cours (secousse)
    const [paneH, setPaneH] = useState(150);
    const solved = blanks.length > 0 && blanks.every((b) => filled.has(b));

    function pick(letter) {
        if (solved) return;
        const up = String(letter).toUpperCase();
        const hits = blanks.filter(
            (b) => !filled.has(b) && String(letters[b]).toUpperCase() === up
        );
        if (hits.length) {
            setWrong(null);
            setFilled((prev) => {
                const n = new Set(prev);
                hits.forEach((b) => n.add(b));
                return n;
            });
        } else {
            setWrong(up);
        }
    }

    const status = solved
        ? { text: spec.solvedLabel || "", cls: "text-emerald-600 dark:text-emerald-400" }
        : wrong
          ? { text: spec.retryLabel || "", cls: "text-red-500" }
          : { text: spec.label || "", cls: "text-gray-700 dark:text-grayDark-200" };

    return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-white/10 dark:bg-white/[0.04]">
            {status.text ? (
                <motion.div
                    key={solved ? "solved" : wrong || "idle"}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mb-4 text-center text-lg font-bold ${status.cls}`}
                >
                    {status.text}
                </motion.div>
            ) : null}

            <div className="mb-5 flex flex-wrap justify-center gap-2.5 text-3xl font-bold uppercase tracking-wide text-gray-800 dark:text-grayDark-100">
                {letters.map((ch, i) => {
                    const hidden = blanks.includes(i) && !filled.has(i);
                    const justFilled = blanks.includes(i) && filled.has(i);
                    return (
                        <motion.span
                            key={i}
                            animate={justFilled ? { scale: [1.4, 1] } : { scale: 1 }}
                            transition={{ duration: 0.3 }}
                            className={
                                justFilled ? "text-emerald-600 dark:text-emerald-400" : ""
                            }
                        >
                            {hidden ? "_" : ch}
                        </motion.span>
                    );
                })}
            </div>

            {!solved && (
                <div className="flex flex-wrap justify-center gap-3">
                    {choices.map((c, i) => {
                        const isWrong =
                            wrong && String(c).toUpperCase() === wrong;
                        return (
                            <motion.button
                                key={i}
                                type="button"
                                onClick={() => pick(c)}
                                animate={
                                    isWrong ? { x: [0, -6, 6, -4, 4, 0] } : { x: 0 }
                                }
                                transition={{ duration: 0.3 }}
                                className={`flex h-14 w-14 items-center justify-center rounded-xl border-2 border-b-4 text-xl font-bold uppercase transition-colors ${
                                    isWrong ? C_BAD : C_NEUTRAL
                                }`}
                            >
                                {c}
                            </motion.button>
                        );
                    })}
                </div>
            )}

            {solved && (reveal.image || reveal.html) && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.92, y: 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    className="mt-1"
                >
                    {reveal.image ? (
                        <img
                            src={reveal.image}
                            alt={reveal.caption || word}
                            className="mx-auto max-h-52 rounded-xl border border-gray-200 dark:border-white/10"
                        />
                    ) : (
                        <BrowserPane
                            html={reveal.html}
                            style={{ height: Math.max(paneH, 120) }}
                            onHeight={(h) => setPaneH(Math.min(38 + h, 360))}
                        />
                    )}
                    {reveal.caption && (
                        <p className="mb-0 mt-3 text-center text-sm text-gray-500 dark:text-grayDark-400">
                            {renderRich(reveal.caption)}
                        </p>
                    )}
                </motion.div>
            )}
        </div>
    );
}

// Interactif CUSTOM (l'escape hatch) : le contenu fournit une mini-app HTML/CSS/JS
// COMPLETE, rendue dans une iframe sandboxee AVEC JavaScript. sandbox="allow-scripts"
// SANS allow-same-origin -> le JS s'execute (origine "null") mais ne peut PAS toucher
// l'app parente : ni DOM parent, ni cookies, ni localStorage, ni navigation. Permet
// LITTERALEMENT n'importe quel element interactif, entierement pilote par JSON (donc
// genere par l'IA et editable en admin). Non note. Hauteur fixee par data.height.
function CustomInteractive({ spec }) {
    const height = Number.isFinite(spec.height)
        ? Math.min(600, Math.max(120, spec.height))
        : 260;
    const html = String(spec.html || "");
    const doc = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">${baseTag()}<style>*{box-sizing:border-box}html,body{margin:0}body{font-family:system-ui,-apple-system,'Segoe UI',sans-serif;color:#1f2937;padding:8px}</style></head><body>${html}</body></html>`;
    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
            <iframe
                title="Interactif"
                sandbox="allow-scripts"
                srcDoc={doc}
                className="block w-full border-0 bg-white"
                style={{ height }}
            />
        </div>
    );
}

// Concept INTERACTIF facon Brilliant : l'apprenant MANIPULE (curseur ou bascule)
// et voit l'apercu changer en direct. Aucune notation, juste la comprehension par
// l'action. Declaratif via data.interactive : le contenu n'ecrit pas de code.
//   { kind:"slider", label, min, max, step, default, template, readout }
//   { kind:"toggle", label, options:[{label,value}], default, template, readout }
// {v} dans template/readout est remplace par la valeur courante.
function InteractiveConcept({ spec }) {
    const t = useTranslations();
    const kind = spec.kind || "slider";
    const options = kind === "toggle" ? spec.options || [] : null;
    const initial =
        spec.default != null
            ? spec.default
            : kind === "toggle"
              ? options.length
                  ? options[0].value
                  : ""
              : spec.min != null
                ? spec.min
                : 0;
    const [v, setV] = useState(initial);
    const [paneH, setPaneH] = useState(150);
    const fill = (str) =>
        (str || "").split("{v}").join(v == null ? "" : String(v));
    const html = fill(spec.template);
    // La pastille de valeur n'a de sens que pour un curseur (ex. "24px") : pour une
    // bascule, on ne l'affiche QUE si l'auteur fournit un readout court explicite
    // (jamais la valeur HTML brute, ni une phrase forcée dans une puce orange).
    const readout = spec.readout
        ? fill(spec.readout)
        : kind === "slider"
          ? String(v)
          : "";

    return (
        // Deux colonnes seulement à partir de `lg`, comme le couple code/aperçu
        // (PreviewSplit) : à `md`, avec le chat BeBot ouvert, l'aperçu tombait
        // sous 180 px et le décor d'une leçon débordait de son cadre. Un widget
        // dont on ne voit plus le rendu ne démontre plus rien.
        <div className="grid gap-3 lg:grid-cols-2">
            <div className="flex flex-col justify-center rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-white/10 dark:bg-white/[0.04]">
                <div className="mb-3 flex items-baseline justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-grayDark-200">
                        {spec.label}
                    </span>
                    {readout ? (
                        <code className={INLINE_CODE}>{readout}</code>
                    ) : null}
                </div>
                {kind === "toggle" ? (
                    <div className="flex flex-wrap gap-2">
                        {options.map((o) => {
                            const active = o.value === v;
                            return (
                                <button
                                    key={String(o.value)}
                                    type="button"
                                    onClick={() => setV(o.value)}
                                    className={`rounded-xl border-2 border-b-4 px-4 py-2 text-sm font-medium transition-all active:translate-y-[2px] active:border-b-2 ${
                                        active ? C_SEL : C_NEUTRAL
                                    }`}
                                >
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <input
                        type="range"
                        min={spec.min}
                        max={spec.max}
                        step={spec.step || 1}
                        value={v}
                        onChange={(e) => setV(Number(e.target.value))}
                        className="reps-range w-full"
                    />
                )}
                {spec.hint && (
                    <p className="mb-0 mt-3 text-xs text-gray-500">{spec.hint}</p>
                )}
            </div>
            <BrowserPane
                html={html}
                style={{ height: Math.max(paneH, 150) }}
                onHeight={(h) => setPaneH(Math.min(38 + h, 420))}
            />
            {/* Pont manipulation -> code réel : le VRAI code, mis à jour en
                direct, pour qu'on voie OÙ la valeur manipulée se pose. Optionnel
                (spec.code), donc sans effet sur les concepts interactifs
                existants. */}
            {spec.code && (
                // min-w-0 : sans ça, l'élément de grille prend la largeur de
                // son contenu (une longue URL non sécable), la colonne s'élargit
                // et TOUTE la grille déborde de l'écran sur mobile.
                <div className="min-w-0 overflow-hidden md:col-span-2">
                    <RepsCodeEditor
                        value={fill(spec.code)}
                        readOnly
                        roHint={t("reps_editor_readonly_tune")}
                        language={looksLikeHtml(spec.code) ? "html" : "css"}
                        // Hauteur mesurée par Monaco (lignes réellement
                        // repliées) : exacte à toutes les largeurs d'écran.
                        autoHeight
                        style={{ height: codeHeightFor(fill(spec.code)) }}
                    />
                </div>
            )}
        </div>
    );
}

// Étape d'ENSEIGNEMENT (pas de notation) : explique + montre un exemple qui s'affiche.
// Repère une "recette"/liste numérotée dans un texte : "intro : 1. a 2. b 3. c".
// Ne se déclenche QUE sur une vraie séquence 1,2,3... (>= 2 items) pour éviter
// les faux positifs sur de la prose qui contient un nombre suivi d'un point.
function parseNumberedList(text) {
    if (typeof text !== "string") return null;
    const markers = text.match(/\d+\.\s+/g) || [];
    const nums = markers.map((s) => parseInt(s, 10));
    const sequential =
        nums.length >= 2 && nums[0] === 1 && nums.every((n, i) => n === i + 1);
    if (!sequential) return null;
    const start = text.indexOf(markers[0]);
    const intro = text
        .slice(0, start)
        .trim()
        .replace(/[:：]\s*$/, "")
        .trim();
    const items = text
        .slice(start)
        .split(/\s*\d+\.\s+/)
        .map((s) => s.trim())
        .filter(Boolean);
    if (items.length < 2) return null;
    return { intro, items };
}

// Exemple "montré" : la recette prose devient une vraie liste d'étapes badgées.
function ExampleSteps({ intro, items }) {
    return (
        <div className="mx-auto w-full max-w-[600px] rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
            {intro && (
                <p className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-gray-500 dark:text-grayDark-400">
                    {renderRich(intro)}
                </p>
            )}
            <ol className="space-y-2.5">
                {items.map((it, i) => (
                    <li key={i} className="flex items-start gap-3">
                        <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary dark:bg-primary/20">
                            {i + 1}
                        </span>
                        <span className="text-sm leading-relaxed text-gray-700 dark:text-grayDark-200">
                            {renderRich(it)}
                        </span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

// Illustration Lottie d'un concept (façon Duolingo/Brilliant) : chargée par NOM,
// d'abord depuis /static/animations/<nom>.json (fichiers déployés avec l'app),
// sinon depuis /api/reps/lottie/<nom> (animations importées sur R2 depuis
// l'éditeur admin, disponibles SANS redéploiement). Le contenu référence
// simplement data.lottie: "nom", quelle que soit la source.
function LottieIllustration({ name, maxWidth, speed, mode }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        const safe = String(name || "").replace(/[^a-z0-9_-]/gi, "");
        if (!safe) return undefined;
        let cancelled = false;
        fetch(`/static/animations/${safe}.json`)
            .then((r) =>
                r.ok ? r.json() : fetch(`/api/reps/lottie/${safe}`).then((r2) => (r2.ok ? r2.json() : null))
            )
            .then((j) => {
                if (!cancelled) setData(j);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [name]);
    // Largeur bornée + responsive (plus petite sur mobile), centrée.
    // data.lottieWidth (100-800 px) remplace la borne par défaut de 260 px ;
    // le mobile reste plafonné plus bas pour ne pas manger l'écran.
    const custom =
        Number.isFinite(maxWidth) && maxWidth > 0
            ? Math.min(800, Math.max(100, Math.round(maxWidth)))
            : null;
    return (
        <div
            className={
                custom
                    ? "mx-auto w-full"
                    : "mx-auto w-full max-w-[200px] sm:max-w-[260px]"
            }
            style={{
                minHeight: data ? undefined : 140,
                ...(custom ? { maxWidth: custom } : {}),
            }}
        >
            {data && (
                <PublicLottieAnimation
                    animation={data}
                    width="100%"
                    speed={
                        typeof speed === "number" && speed > 0 ? speed : 1
                    }
                    mode={
                        ["once", "bounce", "loop-reverse", "once-reverse"].includes(
                            mode
                        )
                            ? mode
                            : "loop"
                    }
                />
            )}
        </div>
    );
}

function Concept({ exercise, onChange, lang }) {
    useEffect(() => {
        onChange(null, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const d = exercise.data || {};
    const steps =
        !d.interactive && !d.code && !d.live ? parseNumberedList(d.example) : null;
    return (
        <div className="space-y-5 text-left">
            {/* Illustration Lottie en tête (facultative), façon Duolingo. */}
            {d.lottie && (
                <LottieIllustration
                    name={d.lottie}
                    maxWidth={d.lottieWidth}
                    speed={d.lottieSpeed}
                    mode={d.lottiePlay}
                />
            )}
            {d.body && (
                // Markdown léger : paragraphes / listes "- " / gras / italique.
                // Le div englobant garde UN seul enfant dans le space-y-5 (comme
                // l'ancien <p>), chaque bloc porte la typo d'origine.
                // data-kw-root + spans numérotés : ancre du karaoké de lecture
                // (RepsLessonPlayer). Inerte tant que BeBot ne lit pas.
                <div data-kw-root="" className="mx-auto w-full max-w-[600px]">
                    {/* max-w-[600px] : la mesure de lecture (45-75 caractères)
                        que le parcours enseigne, appliquée à sa propre prose.
                        Les blocs (code, aperçu, widgets) gardent leur largeur. */}
                    {renderMarkdownBlocks(
                        d.body,
                        "break-words text-base leading-relaxed text-gray-800 dark:text-grayDark-100 sm:text-[17px]",
                        { karaoke: true }
                    )}
                </div>
            )}
            {d.interactive ? (
                (d.interactive.kind || "slider") === "guess" ? (
                    <GuessReveal spec={d.interactive} />
                ) : (d.interactive.kind || "slider") === "custom" ? (
                    <CustomInteractive spec={d.interactive} />
                ) : (
                    <InteractiveConcept spec={d.interactive} />
                )
            ) : d.live && Array.isArray(d.files) && d.files.length > 0 ? (
                <ConceptLive data={d} />
            ) : d.code ? (
                <ConceptCode
                    code={d.code}
                    data={d}
                    lang={lang}
                    preview={!!d.preview}
                />
            ) : steps ? (
                <ExampleSteps intro={steps.intro} items={steps.items} />
            ) : d.example ? (
                // mx-auto max-w-[600px] : l'encadré est de la PROSE, il suit
                // donc la mesure de lecture du body et de la légende, bords
                // alignés au pixel. Seuls les blocs (code, aperçu, widgets)
                // s'autorisent une autre largeur.
                <div className="mx-auto w-full max-w-[600px] rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-gray-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-grayDark-200">
                    {renderMarkdownInline(d.example)}
                </div>
            ) : null}
            {d.caption && (
                <p className="mx-auto w-full max-w-[600px] text-[13px] leading-relaxed text-gray-500 dark:text-grayDark-400">
                    {renderMarkdownInline(d.caption)}
                </p>
            )}
        </div>
    );
}

// Exercice de VRAI code facon Mimo : l'apprenant ECRIT (editeur Monaco) et voit
// le rendu en direct. Correction deterministe et INDULGENTE cote grade.js (on
// verifie le fond via des motifs a inclure, pas la forme). data : { starter?,
// preview?(defaut true), height? }. solution : { include:[...], forbid?:[...] }.
function WriteCode({ exercise, onChange, disabled, verdict }) {
    const t = useTranslations();
    const d = exercise.data || {};
    const preview = d.preview !== false;
    const [val, setVal] = useState(d.starter || "");
    const h = d.height || 240;

    // --- LEÇONS MULTI-FICHIERS (data.files) -------------------------------
    // Un onglet par fichier, un aperçu qui se comporte comme un navigateur
    // devant un dossier : les <link> résolus vers l'onglet CSS, et un clic sur
    // un lien qui OUVRE l'autre page (c'est ça qui rend index.html tangible).
    // La réponse envoyée aux correcteurs devient { files: { nom: contenu } }.
    const multi = Array.isArray(d.files) && d.files.length > 0;
    const starterFiles = useMemo(() => fileMapOf(d.files), [d.files]);
    const entry = useMemo(() => {
        const names = Array.from(starterFiles.keys());
        return d.entry && names.includes(d.entry) ? d.entry : entryFileName(names);
    }, [starterFiles, d.entry]);
    const [filesVal, setFilesVal] = useState(() => Object.fromEntries(starterFiles));
    // La dernière valeur ÉMISE, pas celle du rendu courant : deux frappes
    // peuvent arriver avant que React ait re-rendu (sur Chrome/Edge, la saisie
    // EditContext est une mise à jour de priorité « default »), et une
    // fermeture sur `filesVal` renverrait au player une réponse amputée de la
    // frappe précédente.
    const filesValRef = useRef(filesVal);
    // L'ordre des ONGLETS : les fichiers fournis, puis ceux que l'apprenant a
    // créés (exercices allowCreate), dans l'ordre de création.
    const [tabOrder, setTabOrder] = useState(() => Array.from(starterFiles.keys()));
    // APERÇU VIVANT (`data.live`, parcours JavaScript) : les scripts de la page
    // s'exécutent, et la réponse emporte la photo de la page obtenue
    // (`rendered`), que les règles `solution.rendered` jugent. Les fichiers
    // marqués `readOnly` sont fournis pour être lus, pas modifiés.
    const live = multi && d.live === true;
    const lectureSeule = useMemo(
        () =>
            new Set(
                (Array.isArray(d.files) ? d.files : [])
                    .filter((f) => f && f.readOnly === true)
                    .map((f) => normalizeFileName(f.name))
            ),
        [d.files]
    );
    const gestes = useMemo(() => actionsOf({ actions: d.actions }), [d.actions]);
    const stockageDepart = useMemo(() => storageOf({ storage: d.storage }), [d.storage]);
    const renduRef = useRef(null);
    const [activeTab, setActiveTab] = useState(() => {
        const noms = Array.from(starterFiles.keys());
        // En vivant, on ouvre le fichier à écrire, pas la page fournie.
        if (d.live === true) {
            const libre = noms.find((n) => !lectureSeule.has(n));
            if (libre) return libre;
        }
        return entry || noms[0];
    });
    // La page AFFICHÉE par l'aperçu : elle suit la navigation, pas l'onglet
    // édité (on peut retoucher menu.html en regardant l'accueil).
    const [page, setPage] = useState(() => entry);
    const liveFiles = useMemo(() => new Map(Object.entries(filesVal)), [filesVal]);
    // « Écrit » = un fichier fourni modifié, OU un fichier créé : créer le
    // fichier demandé est déjà une réponse en soi.
    const filesWritten = (m, order) =>
        order.some((n) => !starterFiles.has(n)) ||
        Array.from(starterFiles.keys()).some(
            (n) => String(m[n] ?? "").trim() !== String(starterFiles.get(n) || "").trim()
        );
    function applyFile(v) {
        const next = { ...filesValRef.current, [activeTab]: v };
        filesValRef.current = next;
        setFilesVal(next);
        if (live) {
            // La photo de la page date de la frappe d'avant : « Vérifier »
            // attend celle qui correspond à ce code (onSnapshot, ci-dessous).
            onChange({ files: next, rendered: renduRef.current || "" }, false);
            return;
        }
        onChange({ files: next }, filesWritten(next, tabOrder));
    }
    function recevoirPhoto(dom) {
        renduRef.current = dom;
        onChange(
            { files: filesValRef.current, rendered: dom },
            filesWritten(filesValRef.current, tabOrder)
        );
    }
    // Création par l'apprenant : mêmes règles de nommage que le lint (c'est
    // aussi une leçon de nommage), pas de doublon, 5 fichiers maximum.
    function createFile(rawName) {
        const name = normalizeFileName(rawName);
        if (!isCreatableFileName(name)) return false;
        if (filesValRef.current[name] !== undefined || tabOrder.length >= 5) return false;
        const next = { ...filesValRef.current, [name]: "" };
        const nextOrder = [...tabOrder, name];
        filesValRef.current = next;
        setFilesVal(next);
        setTabOrder(nextOrder);
        setActiveTab(name);
        onChange({ files: next }, filesWritten(next, nextOrder));
        return true;
    }
    // RENOMMER une de ses créations. Les liens qui pointaient vers l'ancien nom
    // ne sont PAS réécrits : un éditeur qui répare tout seul escamoterait
    // justement ce que l'unité 9 enseigne (un lien cherche un nom exact, et il
    // casse quand ce nom n'existe plus). L'apprenant le découvre au clic, comme
    // dans la vraie vie. Seul l'aperçu suit le fichier ouvert, sans quoi il
    // deviendrait blanc sans raison compréhensible.
    function renameFile(oldName, rawName) {
        const r = renameFileInMap(filesValRef.current, tabOrder, oldName, rawName, starterFiles);
        if (!r) return false;
        if (r.inchange) return true;
        const neuf = normalizeFileName(rawName);
        filesValRef.current = r.files;
        setFilesVal(r.files);
        setTabOrder(r.order);
        if (activeTab === oldName) setActiveTab(neuf);
        if (page === oldName) setPage(neuf);
        onChange({ files: r.files }, filesWritten(r.files, r.order));
        return true;
    }
    function removeFile(name) {
        if (starterFiles.has(name)) return; // on ne retire que ses créations
        const next = { ...filesValRef.current };
        delete next[name];
        const nextOrder = tabOrder.filter((n) => n !== name);
        filesValRef.current = next;
        setFilesVal(next);
        setTabOrder(nextOrder);
        if (activeTab === name) setActiveTab(entry || nextOrder[0]);
        if (page === name) setPage(entry || nextOrder.find((n) => n.endsWith(".html")) || null);
        onChange({ files: next }, filesWritten(next, nextOrder));
    }

    // « Verifier » ne s'active que quand l'apprenant a REELLEMENT ecrit : un
    // code de depart intact n'est pas une reponse (aucun exercice n'attend le
    // starter tel quel, c'est un echafaudage a completer). Sans ce garde-fou,
    // le bouton etait actif des le montage et une touche Entree de trop
    // validait le starter -> verdict faux + editeur verrouille alors que la
    // consigne demande encore d'ecrire.
    const starter = d.starter || "";
    const written = (v) => v.trim().length > 0 && v.trim() !== starter.trim();

    useEffect(() => {
        // Amorce : l'etat de completude reflete le code de depart des le montage
        // (des fichiers de depart intacts ne sont pas une reponse).
        if (multi) onChange({ files: Object.fromEntries(starterFiles) }, false);
        else onChange(starter, written(starter));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function applyVal(v) {
        setVal(v);
        onChange(v, written(v));
    }

    // OBJECTIF (data.target) : le rendu à obtenir, affiché AU-DESSUS de l'éditeur.
    // Sans lui, une consigne du genre « deux familles, et sous la première une
    // sous-liste » se devine au lieu de se lire. Volontairement au-dessus et non
    // en troisième colonne : à la largeur d'un téléphone, l'aperçu de l'apprenant
    // et le sien s'écraseraient mutuellement.
    const targetHtml = d.target ? String(d.target) : null;
    const [targetHidden, setTargetHidden] = useState(false);

    return (
        <div>
            {targetHtml && (
                <div className="mb-3">
                    <div className="mb-1.5 flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-grayDark-400">
                            {t("reps_tune_target")}
                        </span>
                        {/* Même geste que « Masquer l'aperçu », donc même
                            bouton : même œil (ouvert = on propose d'afficher,
                            barré = on propose de masquer), même taille, mêmes
                            couleurs. Les marges négatives rendent la surface de
                            survol sans décaler le libellé ni la hauteur du
                            bandeau. */}
                        <button
                            type="button"
                            onClick={() => setTargetHidden((v) => !v)}
                            className="-my-1 -mr-2 flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-grayDark-300"
                        >
                            <SolarIcon
                                name={targetHidden ? "eye-linear" : "eye-closed-linear"}
                                className="text-sm"
                            />
                            {targetHidden ? t("reps_goal_show") : t("reps_goal_hide")}
                        </button>
                    </div>
                    {!targetHidden && (
                        <BrowserPane
                            html={targetHtml}
                            style={{ height: d.targetHeight || 170 }}
                        />
                    )}
                </div>
            )}
            <PreviewSplit
                preview={preview}
                previewFirst={live}
                editor={
                    <RepsCodeEditor
                        value={multi ? (filesVal[activeTab] ?? "") : val}
                        onChange={multi ? applyFile : applyVal}
                        disabled={disabled}
                        language={multi ? fileLanguage(activeTab) : "html"}
                        readOnly={live && lectureSeule.has(activeTab)}
                        tabs={multi ? tabOrder : undefined}
                        activeTab={activeTab}
                        onSelectTab={setActiveTab}
                        // Le + et la croix sont l'ÉDITEUR, pas l'exercice :
                        // toujours là en multi-fichiers, comme dans un vrai
                        // éditeur — on apprend en créant, pas en regardant.
                        // data.allowCreate ne garde que son sens de CONTRAT :
                        // la solution attend un fichier à créer.
                        onAddTab={multi && !disabled && !live ? createFile : undefined}
                        closableTabs={
                            multi && !disabled
                                ? tabOrder.filter((n) => !starterFiles.has(n))
                                : undefined
                        }
                        onCloseTab={multi && !disabled ? removeFile : undefined}
                        onRenameTab={multi && !disabled ? renameFile : undefined}
                        // Seule une reponse FAUSSE est remise en file : on ne
                        // promet un rejeu que dans ce cas-la.
                        roHint={
                            verdict === false
                                ? t("reps_editor_locked_retry")
                                : undefined
                        }
                        style={{ height: h }}
                    />
                }
                previewNode={
                    live ? (
                        <LivePane
                            fileMap={liveFiles}
                            page={page}
                            actions={gestes}
                            stockage={stockageDepart}
                            style={{ height: h }}
                            onSnapshot={recevoirPhoto}
                            onNavigate={(href) => {
                                const cible = navigationTarget(liveFiles, href);
                                if (cible) setPage(cible);
                            }}
                        />
                    ) : multi ? (
                        <BrowserPane
                            html={assemblePage(liveFiles, page)}
                            style={{ height: h }}
                            address={page}
                            onNavigate={(href) => {
                                const cible = navigationTarget(liveFiles, href);
                                if (cible) setPage(cible);
                            }}
                        />
                    ) : (
                        <BrowserPane html={val} style={{ height: h }} />
                    )
                }
            />
            {/* Les motifs `include` ne sont PAS montrés à l'apprenant : normalisés
                pour la correction (`p{background-color:rgba(250,204,21,0.4`), ils se
                lisent comme du code cassé et ne s'expliquent pas tout seuls. Ils
                partent à BeBot une fois le verdict tombé (RepsLessonPlayer,
                getContext) : c'est lui qui les traduit en phrase. */}
        </div>
    );
}

// Exercice de VRAI code PYTHON facon Mimo : l'apprenant ecrit, EXÉCUTE (Pyodide
// dans un worker), voit la sortie console. Correction sur la SORTIE attendue
// (solution.stdout) et/ou des motifs de code (include/forbid). Le code tourne
// cote client sans SharedArrayBuffer (aucun en-tete d'isolation requis) : pas
// d'input() (leve une erreur claire). data : { starter?, height? }.
function PythonCode(props) {
    return <CodeRunner {...props} language="python" />;
}

// js_code : le jumeau JavaScript. Les leçons de ce parcours se jouent sur le
// pouce : l'éditeur est bas par défaut (une ligne à taper dans un départ déjà
// écrit).
function JsCode(props) {
    return <CodeRunner {...props} language="javascript" />;
}

function CodeRunner({ exercise, onChange, disabled, verdict, language }) {
    const t = useTranslations();
    const d = exercise.data || {};
    const fichier = fileNameFor(language);
    // Meme garde-fou que WriteCode : executer le code de depart INTACT (geste
    // d'exploration naturel) ne doit pas rendre « Verifier » actif.
    const pyStarter = d.starter || "";
    const pyWritten = (v) =>
        v.trim().length > 0 && v.trim() !== pyStarter.trim();
    const [code, setCode] = useState(d.starter || "");
    const [out, setOut] = useState(null); // { stdout, stderr, ok, error, timedOut }
    const [running, setRunning] = useState(false);
    const ranRef = useRef(false);
    // JavaScript : la console montre la commande, la sortie et le prompt, soit
    // trois lignes au moins. Une hauteur d'auteur plus basse (pensée pour
    // l'éditeur d'une ligne de l'app) couperait le prompt ici.
    const h =
        language === "javascript" ? Math.max(d.height || 0, 150) : d.height || 240;

    // Precharge le runtime des l'ouverture de l'exercice (le 1er run est plus rapide).
    useEffect(() => {
        warmFor(language);
        onChange({ code: d.starter || "", stdout: "", ran: false }, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function applyCode(v) {
        setCode(v);
        // Le code a change : la sortie affichee devient obsolete, il faut re-executer
        // avant de pouvoir verifier (la correction se base sur la sortie reelle).
        ranRef.current = false;
        onChange({ code: v, stdout: "", ran: false }, false);
    }

    async function runCode() {
        if (running || disabled) return;
        setRunning(true);
        setOut(null);
        const res = await runCodeFor(language, code, d.inputs);
        setOut(res);
        ranRef.current = true;
        setRunning(false);
        onChange({ code, stdout: res.stdout || "", ran: true }, pyWritten(code));
    }

    return (
        <div>
            <div className="grid gap-3 md:grid-cols-2">
                <RepsCodeEditor
                    value={code}
                    onChange={applyCode}
                    disabled={disabled}
                    roHint={
                        verdict === false
                            ? t("reps_editor_locked_retry")
                            : undefined
                    }
                    language={language}
                    filename={fichier}
                    style={{ height: h }}
                />
                {/* Console */}
                <div
                    className="flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-white/10"
                    style={{ height: h }}
                >
                    <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-white/5 dark:bg-[#192734]">
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-grayDark-300">
                            <SolarIcon
                                name="programming-linear"
                                className="text-sm"
                            />
                            Console
                        </span>
                        <button
                            type="button"
                            onClick={runCode}
                            disabled={running || disabled}
                            className="flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                        >
                            {running ? (
                                <SolarIcon
                                    name="refresh-linear"
                                    className="animate-spin text-xs"
                                />
                            ) : (
                                <SolarIcon
                                    name="play-bold"
                                    className="text-xs"
                                />
                            )}
                            {running ? t("reps_python_running") : t("reps_python_run")}
                        </button>
                    </div>
                    <div className="flex-1 overflow-auto whitespace-pre-wrap bg-[#0E161D] p-3 font-geistMono text-[13px] leading-relaxed text-gray-100">
                        <PythonConsoleBody
                            out={out}
                            running={running}
                            filename={fichier}
                            language={language}
                            // Départ exécuté tel quel : rien ne s'affiche, et
                            // « aucune sortie » ressemble à une panne sur le
                            // premier exercice du parcours. On redit quoi faire.
                            silence={
                                language === "javascript" && !pyWritten(code)
                                    ? t("reps_js_untouched")
                                    : undefined
                            }
                            idle={t("reps_python_hint")}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

// TUNE (façon Brilliant) : l'apprenant MANIPULE un ou deux réglages (curseur
// numérique ou bascule enum) jusqu'à atteindre une cible, avec aperçu live, PUIS
// valide. Noté : la réponse = { clé: valeur } comparée à solution.values en local
// (grade.js) et côté serveur (RepsGrader, parité stricte). data : { template (HTML
// avec {clé} par réglage), controls:[{ key, label, kind?, min, max, step, unit,
// default, options:[{value,label}] }], preview, target? (HTML de l'objectif),
// targetLabel?, resultLabel? }. Réutilise BrowserPane et les styles des toggles.
function Tune({ exercise, onChange, disabled, revealed, onTile }) {
    const t = useTranslations();
    const data = exercise.data || {};
    const controls = Array.isArray(data.controls) ? data.controls : [];
    const preview = !!data.preview;
    const active = !disabled && !revealed;

    const initial = {};
    controls.forEach((c) => {
        const isEnum = c.kind === "enum" || Array.isArray(c.options);
        initial[c.key] =
            c.default != null
                ? c.default
                : isEnum
                  ? c.options && c.options.length
                      ? c.options[0].value
                      : ""
                  : Number.isFinite(c.min)
                    ? c.min
                    : 0;
    });
    const [vals, setVals] = useState(initial);
    const [paneH, setPaneH] = useState(160);
    const [targetHidden, setTargetHidden] = useState(false);

    // Émission initiale : réponse enregistrée mais step PAS complet tant que rien
    // n'a été touché (le bouton Vérifier reste désactivé, façon Brilliant : on
    // valide après avoir bougé quelque chose, pas sur l'état par défaut).
    useEffect(() => {
        onChange(initial, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function set(key, value) {
        if (!active) return;
        const next = { ...vals, [key]: value };
        setVals(next);
        onChange(next, true);
        if (onTile) onTile();
    }

    const fillTemplate = (tpl) => {
        let out = String(tpl || "");
        controls.forEach((c) => {
            const v = vals[c.key];
            out = out.split(`{${c.key}}`).join(v == null ? "" : String(v));
        });
        return out;
    };
    const learnerHtml = fillTemplate(data.template);
    const targetHtml = data.target ? String(data.target) : null;
    // Déclaration CSS en train de se construire (pont manipulation -> code réel) :
    // gabarit avec {clé}, mis à jour live. C'est ce qui fait APPRENDRE la syntaxe,
    // pas seulement matcher un rendu (ex. ".card { padding: 24px; }").
    const declaration = data.declaration ? fillTemplate(data.declaration) : null;

    const Controls = (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
            {controls.map((c) => {
                const v = vals[c.key];
                const isEnum = c.kind === "enum" || Array.isArray(c.options);
                return (
                    <div key={c.key}>
                        <div className="mb-2 flex items-baseline justify-between gap-3">
                            <span className="text-sm font-medium text-gray-700 dark:text-grayDark-200">
                                {c.label || c.key}
                            </span>
                            {!isEnum && (
                                <code className={INLINE_CODE}>
                                    {String(v)}
                                    {c.unit || ""}
                                </code>
                            )}
                        </div>
                        {isEnum ? (
                            <div className="flex flex-wrap gap-2">
                                {(c.options || []).map((o) => {
                                    const on = o.value === v;
                                    return (
                                        <button
                                            key={String(o.value)}
                                            type="button"
                                            disabled={!active}
                                            onClick={() => set(c.key, o.value)}
                                            className={`rounded-xl border-2 border-b-4 px-4 py-2 text-sm font-medium transition-all active:translate-y-[2px] active:border-b-2 ${
                                                on ? C_SEL : C_NEUTRAL
                                            }`}
                                        >
                                            {o.label != null ? o.label : String(o.value)}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <input
                                type="range"
                                min={c.min}
                                max={c.max}
                                step={c.step || 1}
                                value={v}
                                disabled={!active}
                                onChange={(e) => set(c.key, Number(e.target.value))}
                                className="reps-range w-full"
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="space-y-4 text-left">
            {preview &&
                (targetHtml ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <div className="mb-1.5 flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-grayDark-400">
                                    {data.targetLabel || t("reps_tune_target")}
                                </span>
                                {/* Même bouton que « Masquer l'aperçu » : œil
                                    ouvert pour afficher, œil barré pour masquer. */}
                                <button
                                    type="button"
                                    onClick={() => setTargetHidden((v) => !v)}
                                    className="-my-1 -mr-2 flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-grayDark-300"
                                >
                                    <SolarIcon
                                        name={
                                            targetHidden
                                                ? "eye-linear"
                                                : "eye-closed-linear"
                                        }
                                        className="text-sm"
                                    />
                                    {targetHidden ? t("reps_goal_show") : t("reps_goal_hide")}
                                </button>
                            </div>
                            {!targetHidden && (
                                <BrowserPane
                                    html={targetHtml}
                                    style={{ height: Math.max(paneH, 140) }}
                                />
                            )}
                        </div>
                        <div>
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
                                {data.resultLabel || t("reps_tune_result")}
                            </div>
                            <BrowserPane
                                html={learnerHtml}
                                style={{ height: Math.max(paneH, 140) }}
                                onHeight={(h) => setPaneH(Math.min(38 + h, 420))}
                            />
                        </div>
                    </div>
                ) : (
                    <BrowserPane
                        html={learnerHtml}
                        style={{ height: Math.max(paneH, 150) }}
                        onHeight={(h) => setPaneH(Math.min(38 + h, 420))}
                    />
                ))}
            {declaration && (
                <RepsCodeEditor
                    value={declaration}
                    readOnly
                    // Ce bloc n'est pas un exemple fige : il se recalcule a
                    // chaque mouvement des reglages du dessus.
                    roHint={t("reps_editor_readonly_tune")}
                    language="css"
                    // PAS « styles.css » en dur : à l'unité 4, le fichier CSS
                    // à part n'est pas enseigné (l'unité 4 leçon 1 le promet
                    // « plus loin »), et cette déclaration vit dans le bloc
                    // <style> de la page. L'étiquette dirait donc à l'apprenant
                    // qu'il regarde un fichier qui n'existe pas encore.
                    filename={data.declarationFilename || null}
                    // Même correctif que le concept interactif : hauteur
                    // mesurée, sinon une déclaration longue est coupée.
                    autoHeight
                    style={{ height: codeHeightFor(declaration) }}
                />
            )}
            {Controls}
        </div>
    );
}

const RENDERERS = {
    concept: Concept,
    tune: Tune,
    mcq: Mcq,
    multi_select: MultiSelect,
    type_answer: TypeAnswer,
    fill_blank: FillBlank,
    tap_tokens: TapTokens,
    reorder: ReorderExercise,
    drop_blocks: DropBlocks,
    match_pairs: MatchPairs,
    drag_drop: DragDrop,
    write_code: WriteCode,
    python_code: PythonCode,
    js_code: JsCode,
    find_error: FindError,
    predict_output: PredictOutput,
    tap_target: TapTarget,
};

// Largeur max d'une image d'étape : celle de l'auteur (imageWidth/image2Width)
// si elle est valide, sinon 420 px — et jamais PLUS de 420 px (la largeur
// historique du player). Le min() CSS la borne aussi par le conteneur.
function clampImageWidth(w) {
    const n =
        typeof w === "number" && Number.isFinite(w) && w > 0
            ? Math.round(w)
            : 420;
    return Math.min(n, 420);
}

export default function RepsExercise(props) {
    const t = useTranslations();
    const Renderer = RENDERERS[props.exercise.type];
    const reduce = useReducedMotion();
    const controls = useAnimation();

    useEffect(() => {
        // Pas de halo vert sur tout l'element (juge moche) ; seul un petit shake sur erreur.
        if (props.verdict === false && !reduce) {
            controls.start({
                x: [0, -10, 10, -8, 8, -4, 0],
                transition: { duration: 0.45 },
            });
        } else {
            controls.start({ x: 0 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.verdict]);

    if (!Renderer) {
        return (
            <p className="text-sm text-gray-500 dark:text-grayDark-400">
                {t("reps_unsupported_exercise_type", {
                    type: props.exercise.type,
                })}
            </p>
        );
    }
    const d = props.exercise.data || {};
    // Images d'illustration (tous types) : une ou deux, chacune bornée par la
    // largeur de l'auteur (imageWidth/image2Width, 420 px max) via min() CSS,
    // jamais de débordement (leçon apprise : min-w-0 / max-w stricts sur
    // mobile). À deux : côte à côte quand l'écran le permet (cap à 50% du
    // conteneur dès sm:), sinon la seconde passe dessous via flex-wrap.
    const imgs = [
        { src: d.image, alt: d.imageAlt, width: d.imageWidth },
        { src: d.image2, alt: d.image2Alt, width: d.image2Width },
    ].filter((im) => typeof im.src === "string" && im.src);
    return (
        <BoxModelContext.Provider
            value={d.boxModel === "content-box" ? "content-box" : "border-box"}
        >
        <motion.div animate={controls} className="rounded-2xl">
            {imgs.length > 0 && (
                <div className="mb-5 flex flex-wrap items-start justify-center gap-3">
                    {imgs.map((im, i) => (
                        <div
                            key={i}
                            className={`flex min-w-0 max-w-full justify-center${
                                imgs.length > 1
                                    ? " sm:max-w-[calc(50%-6px)]"
                                    : ""
                            }`}
                        >
                            <img
                                src={im.src}
                                alt={
                                    typeof im.alt === "string" ? im.alt : ""
                                }
                                loading="lazy"
                                style={{
                                    maxWidth: `min(100%, ${clampImageWidth(im.width)}px)`,
                                }}
                                className="max-h-56 w-auto rounded-xl object-contain sm:max-h-64"
                            />
                        </div>
                    ))}
                </div>
            )}
            <Renderer {...props} />
        </motion.div>
        </BoxModelContext.Provider>
    );
}
